const Promise = require('bluebird');
const Reflux = require('@muzzylane/reflux');
const uuid = require('uuid');

const _ = {
    cloneDeep:  require('lodash/cloneDeep'),
    compact:    require('lodash/compact'),
    find:       require('lodash/find'),
    findIndex:  require('lodash/findIndex'),
    filter:     require('lodash/filter'),
    forEach:    require('lodash/forEach'),
    map:        require('lodash/map'),
    isEmpty:    require('lodash/isEmpty'),
    isEqual:    require('lodash/isEqual'),
    sortBy:     require('lodash/sortBy'),
};

const FeatureParams = require('@muzzylane/nova/lib/client_tools').FeatureParams;

const Globals = require('../../../app/globals');
const ShortCourseUtil = require('../../../app/shared/ShortCourseUtil');
const ShortCourseConstants = require('../../../app/shared/ShortCourseConstants');
const COACH_SCRIPT_ID = ShortCourseConstants.COACH_SCRIPT_ID;

const ShortCourseEditorStore = require('./ShortCourseEditorStore');
const AssetUtil = require('../util/asset');
const VoiceStore = require('../stores/VoiceStore');
const ShortCourseInstanceStore = require('../stores/ShortCourseInstanceStore');
const ShortCourseInstanceActions = require('../actions/ShortCourseInstanceActions');
const AccountActions = require('../actions/AccountActions');

// current main focus of gameplay
const FLOW_FOCI = {
    FlowMap: 'flow_map',                            // looking at the progress map
    SingleNode: 'single_node',                      // looking at a single node (aka recommendation screen)
    ActivityAttempt: 'activity_attempt',            // attempting an activity
    ContentBlockLink: 'content_block_link',         // following a content block link
    ChatBotAttempt: 'chatbot_attempt',              // talking to an AI tutor
    WelcomeScreen: 'welcome_screen',                // looking at intro rich text
    AvatarSelectScreen: 'avatar_select_screen',     // picking an avatar
    AttemptCompleted: 'attempt_completed_screen',   // showing the 'payout' page
    CourseCompleted: 'course_completed_screen',     // showing the end screen page
    ErrorScreen: 'badge_problem_screen',     // the course specifies a badge but we don't have enough user information 
};

const REPORT_CARD_PHASE = ShortCourseConstants.REPORT_CARD_PHASE;

function _charCodeSum(str) {
    let t=0;
    for (var p=0; p<str.length; ++p) {
        t += (p+1)*1024 + str.charCodeAt(p);
    }
    return t;
}


// params = {doc, preview (bool) }
function ShortCoursePlayStore(params) {
    const { doc: unconvertedDoc, activities, activity_types, templates, user, currentAttempt, channelId, shortCourseId, ...startParams } = params;
    let doc = ShortCourseEditorStore.applyConversions(unconvertedDoc); // in case there are any data conversions for old client_content
    this.doc = doc;
    this.activities = activities; // only needed for preview
    this.templates = templates; // only needed for preview
    if (!doc.user) {
        doc.user = user; // normalize doc so it contains user
    }
    this.user_id = user.id;
    this.user_hash = user.hash;
    this.channelId = channelId;
    this.shortCourseId = shortCourseId;
    this.startParams = startParams;
    
    this.actions = Reflux.createActions([
        "startPlay",                // no parameters
        "selectNode",               // node_id
        "selectContentBlock",       // hash
        "attemptContentBlock",      // node_id, hash
        "setFlowFocus",             // flowFocus
        "advanceCoachScript",       // coach_script_id
        "advanceReportCard",        // optional fnCallback(newPhase)
        "addContentBlockAttempt",   // hash, scoreData
        "finishChatActivity",       // hash, scores, time_played
        "refreshDoc",               // no parameters
        "exitAvatarSelect",         // no parameters
        "exitWelcomeScreen",        // no parameters
        "goToRecommendedNode",      // no parameters
        "goToPostAttemptScreen",    // no parameters
//        "goToAvatarSelectScreen",   // no parameters
        "setAvatar",                // name,hash
        "updatePersistentFlags",    // no parameters
    ]);
    
    this.currentAttempt = currentAttempt;
    this.previewContentBlockAttempts = [];

    // variables for store
    let storeState = {};
    const playStore = this;
    // let content_block_statuses = [];
    
    // precalculated data
    // this.left_to_right_sorted_nodes = this._calcLeftToRightSort();

    this.nodes_in_order = this._calcNodeOrder();
    
    // create store
    this.store = Reflux.createStore({
        listenables: [playStore.actions],
        init: function() {
            if (playStore.currentAttempt) {
                let cb = _.find(doc.content_blocks,{hash: playStore.currentAttempt.content_block_hash});
                storeState = {
                    flowFocus: (cb.type ===  Globals.short_course.content_block_type.CHATBOT) ? FLOW_FOCI.ChatBotAttempt : FLOW_FOCI.ActivityAttempt,
                    node_id: playStore.currentAttempt.node_id,
                    content_block_hash: playStore.currentAttempt.content_block_hash,
                };
                if (!storeState.node_id) {
                    storeState.node_id = playStore.getArbitraryIncompleteNode(storeState.node_id, storeState.content_block_hash)
                }
            }
        },
        
        getState: function() {
            return storeState;
        },

        onStartPlay: function() {
			if (playStore.getIsPreview()) {
				playStore.initPreviewInstance();
			}
            let { /*content_block_statuses, */ node_id, content_block_hash, attempt_count } = playStore.processAttempts();
            if (node_id && playStore.isNodeComplete(node_id)) {
                node_id = playStore.getArbitraryIncompleteNode();
            }
            let sawWelcomeScreen = attempt_count ? true : false; // don't show welcome screen if we have any attempts
            if (!node_id) {
                if (storeState.node_id) {
                    node_id = playStore.getArbitraryIncompleteNode(storeState.node_id);
                    if (!node_id) {
                        playStore.getArbitraryIncompleteNode(); // try again without the starting node
                    }
                }
                else {
                    node_id = playStore.getArbitraryIncompleteNode();
                }
            }
            
            if (doc.client_content.voiceOptions && doc.client_content.voiceOptions.engine && doc.client_content.voiceOptions.voice_id) {
                playStore.prepVoiceAudio();
            }

            let hasAvatar = playStore.hasAvatar();
            let flowFocus;
            if (!hasAvatar) {
                flowFocus = FLOW_FOCI.AvatarSelectScreen;
            }
            else if (!sawWelcomeScreen && playStore.hasWelcomeScreen()) {
                flowFocus = FLOW_FOCI.WelcomeScreen;
            }
            else {
                flowFocus = FLOW_FOCI.SingleNode;
            }
            
            // check badge requirements
            if (!playStore.getIsPreview() && doc.badge_parameters && doc.badge_parameters.issueBadge) {
                let haveFirstName = false;
                let haveLastName = false;
                let haveEmail = false;
                if (doc.user) {
                    if (doc.user.first_name && doc.user.first_name.trim()) {
                        haveFirstName = true;
                    }
                    if (doc.user.last_name && doc.user.last_name.trim()) {
                        haveLastName = true;
                    }
                    let email = doc.user.email || doc.user.third_party_email;
                    if (email && email.trim()) {
                        haveEmail = true;
                    }
                }
                if (!haveFirstName || !haveLastName || !haveEmail) {
                    flowFocus = FLOW_FOCI.ErrorScreen;
                    playStore.errorInfo = {
                        title: "Badge Requirements Missing",
                        text: "This Short Course specifies a badge which requires the user's first name, last name, and email but this information is not available."
                    }
                }
            }
            
            storeState = {
                flowFocus,
                node_id,
                content_block_hash,
                sawWelcomeScreen,
                // content_block_statuses,
            };
            let { coach_script_id, coachVariables, coachConditions, remedial_skills } = playStore.calculateCoachVariables();
            storeState.coach_script_id = coach_script_id;
            storeState.coachVariables = coachVariables;
            storeState.coachConditions = coachConditions;
            storeState.coach_script_action_offset = playStore.getFirstCoachScriptOffset(coach_script_id, coachConditions);
            storeState.coach_script_report_card_phase = REPORT_CARD_PHASE.START;
            storeState.coach_remedial_skills = remedial_skills;
            storeState.client_state = playStore.updatePersistentClientState(storeState);
            this.emitStatus();
        }, // onStartPlay

        onRefreshDoc: function() {
            console.log(`onRefreshDoc called`);
            doc = playStore.doc; // make sure our variable matches what playStore has.
            if (storeState.node_id) {
                storeState.flowFocus = FLOW_FOCI.AttemptCompleted;
                let { node_id, content_block_hash } = playStore.processAttempts();
                storeState.recommended_node_id = playStore.getArbitraryIncompleteNode(node_id);
                let { coach_script_id, coachVariables, coachConditions, remedial_skills, justFinishedTutorial } = playStore.calculateCoachVariables();
                if (justFinishedTutorial) {
                    storeState.flowFocus = FLOW_FOCI.SingleNode; // skip AttemptCompleted since the tutorial doesn't have an attempt complete script
                }
                if (coach_script_id !== storeState.coach_script_id) {
                    storeState.coach_script_action_offset = playStore.getFirstCoachScriptOffset(coach_script_id, coachConditions);
                    storeState.coach_script_report_card_phase = REPORT_CARD_PHASE.START;
                }
                storeState.coach_script_id = coach_script_id;
                storeState.coachVariables = coachVariables;
                storeState.coachConditions = coachConditions;
                storeState.coach_remedial_skills = remedial_skills;
                storeState.client_state = playStore.updatePersistentClientState(storeState);
                this.doUpdatePersistentFlags(coachConditions);
                this.emitStatus();
            }
        },
        
        onGoToPostAttemptScreen: function() {
            delete storeState.priorCoachState;
            storeState.flowFocus = FLOW_FOCI.AttemptCompleted;
            let { coach_script_id, coachVariables, coachConditions, remedial_skills, justFinishedTutorial } = playStore.calculateCoachVariables();
            if (justFinishedTutorial) {
                storeState.flowFocus = FLOW_FOCI.SingleNode; // skip AttemptCompleted since the tutorial doesn't have an attempt complete script
            }
            if (coach_script_id !== storeState.coach_script_id) {
                storeState.coach_script_action_offset = playStore.getFirstCoachScriptOffset(coach_script_id, coachConditions);
                storeState.coach_script_report_card_phase = REPORT_CARD_PHASE.START;
            }
            storeState.coach_script_id = coach_script_id;
            storeState.coachVariables = coachVariables;
            storeState.coachConditions = coachConditions;
            storeState.coach_remedial_skills = remedial_skills;
            storeState.recommended_node_id = playStore.getArbitraryIncompleteNode(storeState.node_id);
            storeState.client_state = playStore.updatePersistentClientState(storeState);
            this.emitStatus();
        },
        
        onExitAvatarSelect: function() {
            if (storeState.sawWelcomeScreen || !playStore.hasWelcomeScreen()) {
                return this.onGoToRecommendedNode();
            }
            storeState.flowFocus = FLOW_FOCI.WelcomeScreen;
            let { coach_script_id, coachVariables, coachConditions, remedial_skills } = playStore.calculateCoachVariables();
            storeState.coach_script_id = coach_script_id;
            storeState.coach_script_action_offset = playStore.getFirstCoachScriptOffset(coach_script_id, coachConditions);
            storeState.coachVariables = coachVariables;
            storeState.coachConditions = coachConditions;
            storeState.coach_remedial_skills = remedial_skills;
            storeState.client_state = playStore.updatePersistentClientState(storeState);
            this.emitStatus();
        },
        
        onExitWelcomeScreen: function() {
            storeState.priorCoachState = {
                coach_script_id:  storeState.coach_script_id,
                coachVariables: _.cloneDeep(storeState.coachVariables),
                coach_remedial_skills: _.cloneDeep(storeState.coach_remedial_skills)
            }
            storeState.flowFocus = FLOW_FOCI.SingleNode;
            let { coach_script_id, coachVariables, coachConditions, remedial_skills } = playStore.calculateCoachVariables();
            storeState.coach_script_id = coach_script_id;
            storeState.coach_script_action_offset = playStore.getFirstCoachScriptOffset(coach_script_id, coachConditions);
            storeState.coachVariables = coachVariables;
            storeState.coachConditions = coachConditions;
            storeState.coach_remedial_skills = remedial_skills;
            storeState.client_state = playStore.updatePersistentClientState(storeState);
            this.emitStatus();
        },
        
//        onGoToAvatarSelectScreen: function() {
//            storeState.flowFocus = FLOW_FOCI.AvatarSelectScreen;
//            let { coach_script_id, coachVariables, coachConditions, remedial_skills } = playStore.calculateCoachVariables();
//            storeState.coachVariables = coachVariables;
//            storeState.coachConditions = coachConditions;
//            storeState.coach_remedial_skills = remedial_skills;
//            this.emitStatus();
//        },
        
        onGoToRecommendedNode: function() {
            // see if we want to keep the prior coach script nodes and variables
            // for use with the recommendation coach script
            delete storeState.priorCoachState;
            if (storeState.flowFocus === FLOW_FOCI.AttemptCompleted) {
                storeState.priorCoachState = {
                    coach_script_id:  storeState.coach_script_id,
                    coachVariables: _.cloneDeep(storeState.coachVariables),
                    coach_remedial_skills: _.cloneDeep(storeState.coach_remedial_skills)
                }
            }

            if (playStore.doc.user.course_complete) {
                storeState.flowFocus = FLOW_FOCI.CourseCompleted;
                let { coach_script_id, coachVariables, coachConditions, remedial_skills } = playStore.calculateCoachVariables();
                if (coach_script_id !== storeState.coach_script_id) {
                    storeState.coach_script_action_offset = playStore.getFirstCoachScriptOffset(coach_script_id, coachConditions);
                    storeState.coach_script_report_card_phase = REPORT_CARD_PHASE.START;
                }
                storeState.coach_script_id = coach_script_id;
                storeState.coachVariables = coachVariables;
                storeState.coachConditions = coachConditions;
                storeState.coach_remedial_skills = remedial_skills;
                storeState.client_state = playStore.updatePersistentClientState(storeState);
                this.emitStatus();
                return;
            }
            
            if (storeState.recommended_node_id) {
                storeState.node_id = storeState.recommended_node_id;
                delete storeState.recommended_node_id;
            }
            let node_id = playStore.getArbitraryIncompleteNode(storeState.node_id);
            if (!node_id && storeState.node_id) {
                node_id = playStore.getArbitraryIncompleteNode(); // try again without the starting node
            }
            if (node_id) { // we confirmed that we have a node_id for an incomplete node
                storeState.flowFocus = FLOW_FOCI.SingleNode;
                storeState.node_id = node_id;
                let { coach_script_id, coachVariables, coachConditions, remedial_skills } = playStore.calculateCoachVariables();
                if (coach_script_id !== storeState.coach_script_id) {
                    storeState.coach_script_action_offset = playStore.getFirstCoachScriptOffset(coach_script_id, coachConditions);
                    storeState.coach_script_report_card_phase = REPORT_CARD_PHASE.START;
                }
                storeState.coach_script_id = coach_script_id;
                storeState.coachVariables = coachVariables;
                storeState.coachConditions = coachConditions;
                storeState.coach_remedial_skills = remedial_skills;
                storeState.client_state = playStore.updatePersistentClientState(storeState);
                this.emitStatus();
                return;
            }
            // no incomplete nodes.  Try again without a previous node_id
            if (doc.nodes.length) {
                storeState.flowFocus = FLOW_FOCI.CourseCompleted;
            }
            else {
                storeState.flowFocus = FLOW_FOCI.FlowMap;
            }
            let { coach_script_id, coachVariables, coachConditions, remedial_skills } = playStore.calculateCoachVariables();
            if (coach_script_id !== storeState.coach_script_id) {
                storeState.coach_script_action_offset = playStore.getFirstCoachScriptOffset(coach_script_id, coachConditions);
                storeState.coach_script_report_card_phase = REPORT_CARD_PHASE.START;
            }
            storeState.coach_script_id = coach_script_id;
            storeState.coachVariables = coachVariables;
            storeState.coachConditions = coachConditions;
            storeState.coach_remedial_skills = remedial_skills;
            storeState.client_state = playStore.updatePersistentClientState(storeState);
            this.emitStatus();
        },
        
        onSelectNode: function(params) {
            let { node_id } = params;
            if (node_id) {
                storeState.flowFocus = FLOW_FOCI.SingleNode;
            }
            else if (storeState.flowFocus === FLOW_FOCI.SingleNode) {
                storeState.flowFocus = FLOW_FOCI.FlowMap;
            }
            storeState.node_id = node_id;
            let { coach_script_id, coachVariables, coachConditions, remedial_skills } = playStore.calculateCoachVariables();
            if (coach_script_id !== storeState.coach_script_id) {
                storeState.coach_script_action_offset = playStore.getFirstCoachScriptOffset(coach_script_id, coachConditions);
                storeState.coach_script_report_card_phase = REPORT_CARD_PHASE.START;
            }
            storeState.coach_script_id = coach_script_id;
            storeState.coachVariables = coachVariables;
            storeState.coachConditions = coachConditions;
            storeState.coach_remedial_skills = remedial_skills;
            storeState.client_state = playStore.updatePersistentClientState(storeState);
            this.emitStatus();
        },
        
        selectContentBlock: function(params) {
            let { hash } = params;
            storeState = { ... storeState, content_block_hash: hash };
            this.emitStatus();
        },

        onAttemptContentBlock: function(params) {
            let { node_id, content_block_hash } = params;
            let cb = _.find(doc.content_blocks,{hash: content_block_hash});
            if (!cb) {
                storeState = { ... storeState, error: `Content Block ${content_block_hash} not found`};
            }
            else {
                let flowFocus;
                switch(cb.type) {
                    case Globals.short_course.content_block_type.ACTIVITY:
                        if (!playStore.getIsPreview()) {
                            console.log("attemptContentBlock only for preview"); // unexpected
                            return;
                        }
                        flowFocus = FLOW_FOCI.ActivityAttempt;
                        break;
                    case Globals.short_course.content_block_type.LINK:
                        flowFocus = FLOW_FOCI.ContentBlockLink;
                        break;
                    case Globals.short_course.content_block_type.CHATBOT:
                        flowFocus = FLOW_FOCI.ChatBotAttempt;
                        break;
                }
                storeState = { ... storeState, node_id: node_id, content_block_hash:content_block_hash, flowFocus: flowFocus };
            }
            this.emitStatus();
        },
        
        onSetFlowFocus: function(params) {
            let { flowFocus } = params;
            storeState = { ... storeState, flowFocus: flowFocus };
            this.emitStatus();
        },
        
        onAdvanceCoachScript: function() {
            let nextOffset = playStore.getNextCoachScriptOffset(storeState.coach_script_id, storeState.coach_script_action_offset);
            storeState = {...storeState, coach_script_action_offset: nextOffset };
            this.emitStatus();
        },
        
        onAdvanceReportCard: function(fnCallback) {
            let nextPhase = storeState.coach_script_report_card_phase + 1;
            // skip empty phases
            if ((nextPhase === REPORT_CARD_PHASE.HIGHLIGHT_PASSED) && storeState.coachVariables && !storeState.coachVariables.passedSkills) {
                nextPhase++;
            }
            if ((nextPhase === REPORT_CARD_PHASE.HIGHLIGHT_FAILED) && storeState.coachVariables && !storeState.coachVariables.failedSkills) {
                nextPhase++;
            }
            if (nextPhase <= REPORT_CARD_PHASE.END ) {
                storeState = {...storeState, coach_script_report_card_phase: nextPhase };
                this.emitStatus();
                if (fnCallback) {
                    setTimeout(()=>{
                        fnCallback(nextPhase);
                    },0);
                }
            }
        },

        // This action is used when previewing to simulate getting a score.
        // During actual play, the activity would register an attempt via it's usual mechanism
        onAddContentBlockAttempt: function(params) {
            let {hash, scoreData, scoresBySubskillHash} = params;
            const node_id = storeState.node_id;
            let cb = _.find(doc.content_blocks,{hash});
            if (!cb) {
                console.log(`content block ${hash} not found`);
                return;
            }
            const isBot = (cb.type === Globals.short_course.content_block_type.CHATBOT);
			let nr = cb.node_relations[node_id];
			let activity_type = nr ? nr.activity_type : cb.activity_type;
			let activityTypeObj = _.find(doc.activity_types,{id:activity_type});
            let hasNoImpact = activityTypeObj && (activityTypeObj.credit_type === Globals.short_course.node_content_credit_type.TUTORIAL);
            if (hasNoImpact) {
                playStore.previewContentBlockAttempts.push({
                    attempt_time: new Date(),
                    node_id,
                    hash,
                    scoreData,scoresBySubskillHash,
                    activityTypeObj,
                    skillsInvolved: [],
                    subskillImpacts: [],
                    fail_count: 0, 
                    remedial: false,
                    passed_gate: false,
                });
                return;
            }

            let prior_subskill_arr = [];
            _.forEach(cb.subskill_relations,(xref,hash)=>{
                let subskill_id = _.findIndex(doc.subskills,{hash}); // simulated subskill_id for preview
                let ss = doc.subskills[subskill_id];
                if (ss && ss.node_relations[node_id]) {
                    let user_subskill_state = ss.node_relations[node_id].subskill_states[playStore.user_id];
                    let prior_state = _.cloneDeep(user_subskill_state);
                    prior_state.subskill_id = subskill_id;
                    prior_state.hash = hash;
                    prior_subskill_arr.push(prior_state);
                }
            });
            
            let prior_attempts = _.filter(playStore.previewContentBlockAttempts,{node_id,hash});
            let keyedScResults = {};
            let score;
            if (!isBot) {
                // generate score data as it is sent from nova activity 'complete' endpoint
                _.forEach(cb.subskill_relations,(xref,hash)=>{
                    if (xref.cb_score_type === Globals.short_course.content_block_score_req_type.OBJECTIVE) {
                        let cat = _.find(scoreData.score_categories,{category_id: xref.objective_name});
                        keyedScResults[xref.objective_name] = (cat.score * 100 / cat.max_score);
                    }
                });
                score = scoreData.score;
            }
            
            if (activityTypeObj) {
                let { subskills, attempt_subskill_impacts } = ShortCourseUtil.getUpdatedSubskills({
                    prior_subskill_arr, 
                    activityTypeObject: activityTypeObj, 
                    contentAttemptId: `preview_attempt_${1+playStore.previewContentBlockAttempts.length}`, 
                    attemptNumber: 1 + prior_attempts.length,
                    subskillRelations: _.map(cb.subskill_relations,(entry,hash)=>{
                        let subskill_id = _.findIndex(doc.subskills,{hash}); // simulated subskill_id for preview
                        return { ...entry,
                            mzl_course_version_subskill_id: subskill_id,
                            subskill_hash: hash,
                        };
                    }),
                    score, keyedScResults, // scores from nova activity
                    scoresBySubskillHash, // scores from chatbot
                    courseInstance: doc, 
                    logger: {
                        debug: console.log,
                        info: console.log,
                        warn: console.log,
                        error: console.log,
                    },
                });

                let skillsInvolved = [];
                let subskillImpacts = attempt_subskill_impacts;
				let remedial = (activityTypeObj.credit_type === Globals.short_course.node_content_credit_type.PRACTICE);
				let fail_count = 0; 
                let passed_gate = false;
                if (subskills) {
                    _.forEach(attempt_subskill_impacts,(impact)=>{
                        let ss = _.find(doc.subskills,{hash: impact.subskill_hash});
                        let subskill_score = impact.score;
                        let passed_subskill = (subskill_score >= doc.mastery_score);
                        if (!passed_subskill) {
                            fail_count++;
                        }
                        //console.log(`${ss.display_name} ${passed_subskill ? "passed" : "failed"} with score ${subskill_score}`);
                        skillsInvolved.push({
                            subskill: ss,
                            score: impact.score,
                            passed: passed_subskill,
                            gate_impact: impact.gate_impact,
                            remedial_impact: impact.remedial_impact,
                        });
                    });
                    let uninvolved_subskill_ids = [];
                    _.forEach(cb.subskill_relations,(xref,hash)=>{
                        let impactEntry = _.find(attempt_subskill_impacts,{subskill_hash: hash});
                        if (!impactEntry) {
                            let subskill_id = _.findIndex(doc.subskills,{hash}); // simulated subskill_id for preview
                            uninvolved_subskill_ids.push(subskill_id);
                        }
                    });

                    let not_passed_count = 0;
                    _.forEach(uninvolved_subskill_ids,(subskill_id)=>{
                        let oldEntry = _.find(prior_subskill_arr,{subskill_id});
                        let newEntry = _.find(subskills,{subskill_id});
                        let effectiveEntry = newEntry || oldEntry;
                        if (effectiveEntry.remedial) {
                            not_passed_count++;
                        }
                    });
                    passed_gate = (skillsInvolved.length && !fail_count && !not_passed_count) ? true : false; // true if we passed the assessment
                    //if (passed_gate) {
                    //    let activity = _.find(playStore.activities,{activity_id: cb.nova_activity_id});
                    //    console.log(`passed_gate for cb ${activity.title} ${hash} is true because ${skillsInvolved.length} && ${!fail_count} && ${!not_passed_count}.`);
                    //    console.log("scoreData:",JSON.stringify(scoreData));
                    //    console.log("keyedScResults:",JSON.stringify(keyedScResults));
                    //}
                    
                    // now update our states
                    _.forEach(cb.subskill_relations,(xref,hash)=>{
                        let subskill_id = _.findIndex(doc.subskills,{hash}); // simulated subskill_id for preview
                        let ss = doc.subskills[subskill_id];
                        if (ss && ss.node_relations[node_id]) {
                            let oldEntry = ss.node_relations[node_id].subskill_states[playStore.user_id];
                            let newEntry = _.find(subskills,{subskill_id});
                            if (newEntry) {
                                let updatedEntry = {...oldEntry, ...newEntry };
                                //console.log("oldEntry",JSON.stringify(oldEntry));
                                //console.log("newEntry",JSON.stringify(newEntry));
                                //console.log("updatedEntry",JSON.stringify(updatedEntry));
                                ss.node_relations[node_id].subskill_states[playStore.user_id] = updatedEntry;
                            }
                        }
                    });
                    
                }

                playStore.previewContentBlockAttempts.push({
                    attempt_time: new Date(),
                    node_id,
                    hash,
                    scoreData,scoresBySubskillHash,
                    activityTypeObj,
                    skillsInvolved,
                    subskillImpacts,
                    fail_count, 
                    remedial,
                    passed_gate,
                });
			}
			else {
				console.log(`no activity type found for cb ${hash} node ${node_id}`);
			}
			
            // check for course complete
            let courseResult = ShortCourseUtil.getCourseOverallResult(doc);
            if (courseResult.complete) {
                console.log("preview is complete");
                if (doc.user) {
                    doc.user.course_complete = true;
                    doc.user.course_score = courseResult.score;
                }
            }
			
            this.emitStatus();
        }, // onAddContentBlockAttempt
        
        onFinishChatActivity: function(hash,scoresBySubskillHash, time_played) {
            let cb = _.find(doc.content_blocks,{hash});
            if (!cb) {
                console.log(`content block ${hash} not found`);
                return;
            }

            if (playStore.getIsPreview()) {
                this.onAddContentBlockAttempt({hash, scoresBySubskillHash});
                this.onGoToPostAttemptScreen();
            }
            else {
                let attemptNumber = 1;
                _.forEach(cb.content_block_instances,(cbi)=>{
                    _.forEach(cbi.attempts,(attempt)=>{
                        //let u2a = _.find(cbi.users_to_attempts,{user_id: String(playStore.user_id)});
                        attemptNumber = Math.max(attemptNumber, attempt.attempt + 1);
                    });
                });
                
                const instanceHash = doc.hash;
                ShortCourseInstanceActions.completeChatbotActivity(uuid.v4(),{
                    instanceHash, 
                    nodeId: playStore.getNodeId(), 
                    cbHash: cb.hash,
                    scoresBySubskillHash,
                    attempt: attemptNumber,
                    time_played,
                    successCallback: ()=>{
                        delete storeState.priorCoachState; // clear history (like what happens when 
                        ShortCourseInstanceActions.getShortCourseInstance(uuid.v4(),{
                            instanceHash,
                            // We should go to the post attempt screen due to the doc being updated
                            //successCallback: ()=>{ 
                            //    playStore.actions.goToPostAttemptScreen();
                            //},
                        }); // refresh our data
                    },
                });
            }
        },
        
        onSetAvatar: function(avatar) {
            playStore.setAvatar(avatar);
            this.emitStatus();
        }, // onSetAvatar

        // helper for onUpdatePersistentFlags and other actions that need to update user persistent flags
        doUpdatePersistentFlags: function(coachConditions) {
            if (!playStore.getIsPreview()) {
                let persistentData = playStore.getUserPersistentData();
                let shortCourseFlags = persistentData.shortCourseFlags || {};
                if (coachConditions) {
                    let changed = false;
                    if (coachConditions.firstCheck && !shortCourseFlags.firstCheck) {
                        changed = true;
                        shortCourseFlags.firstCheck = playStore.doc.hash; // first check seen in THIS course instance
                    }
                    if (coachConditions.firstStar && !shortCourseFlags.firstStar) {
                        changed = true;
                        shortCourseFlags.firstStar = playStore.doc.hash; // first star seen in THIS course instance
                    }
                    if (changed) {
                        persistentData.shortCourseFlags = shortCourseFlags;
                        playStore.doc.user.persistant_data.shortCourseFlags = shortCourseFlags; // update local data immediately
                        AccountActions.updateUserPersistentData(uuid.v4(),{
                            userId: playStore.user_id,
                            persistentData
                        });
                    }
                }
            }
        },
        
        onUpdatePersistentFlags: function() {
            this.doUpdatePersistentFlags(playStore.getCoachVariables());
            this.emitStatus();
        },

        emitStatus: function() {
            this.trigger(this.getState());
        },
        
    });
}

// haiku: "anthropic.claude-3-haiku-20240307-v1:0",
// opus: "anthropic.claude-3-opus-20240229-v1:0",
// 3.5 sonnet: "anthropic.claude-3-5-sonnet-20240620-v1:0",
//ShortCoursePlayStore.AI_TUTOR_MODEL_ID = "anthropic.claude-3-haiku-20240307-v1:0";
//ShortCoursePlayStore.AI_TUTOR_MODEL_ID = "anthropic.claude-3-opus-20240229-v1:0";
ShortCoursePlayStore.AI_TUTOR_MODEL_ID = "anthropic.claude-3-5-sonnet-20240620-v1:0";

ShortCoursePlayStore.prototype.getAITutorModelId = function() {
    return ShortCoursePlayStore.AI_TUTOR_MODEL_ID;
}

// returns true of node_a is a prerequisite of node_b
ShortCoursePlayStore.prototype.isPrerequisite = function(node_a,node_b,visited) {
    if (!visited) {
        visited = {};
    }
    else if (visited[node_b.node_id]) {
        return; // calculated by another recursion
    }
    visited[node_b.node_id] = true;
    var isPrereq = false;
    _.forEach(node_b.prereq_node_ids,(node_id)=>{
        if (node_id === node_a.node_id) {
            isPrereq = true;
            return false; // early exit
        }
        if (!visited[node_id]) {
            let pre_node_b = _.find(this.doc.nodes,{node_id});
            if (pre_node_b) {
                let test = this.isPrerequisite(node_a,pre_node_b,visited);
                if (test) {
                    isPrereq = true;
                    return false; // early exit
                }
            }
        }
    });
    return isPrereq;
}

// calculate nodes in order of progression (ending at the capstone)
// based on prerequisites
ShortCoursePlayStore.prototype._calcNodeOrder = function() {
    let sorted_nodes = this.doc.nodes.slice(); // make a copy because sort mutates
    sorted_nodes.sort((a,b)=>{
        if (a.capstone) {
            if (b.capstone) {
                console.log("multiple capstones");
                return 0;
            }
            return 1;
        }
        else if (b.capstone) {
            return -1;
        }
        if (this.isPrerequisite(a,b)) { // is a a prerequisite of b?
            return -1;
        }
        if (this.isPrerequisite(b,a)) { // is b a prerequesite of a?
            return 1;
        }
        return 0;
    })
    return sorted_nodes;
}

// return doc nodes sorted from left to right, then top to bottom (based on position)
//ShortCoursePlayStore.prototype._calcLeftToRightSort = function() {
//    let sorted_nodes = this.doc.nodes.slice(); // make a copy because sort mutates
//    sorted_nodes.sort(function(a,b){
//        if (a.position) {
//            if (b.position) {
//                let dx = ( a.position.x - b.position.x );
//                if (Math.abs(dx) < 20) { // if they are approximately the same place horizontally, sort them top to bottom
//                    let dy = a.position.y - b.position.y;
////                    if (dy < 0) {
////                        console.log(`sorting ${a.name} before ${b.name} due to dy=${dy}. dx was ${dx}`);
////                    }
////                    else if (dy > 0) {
////                        console.log(`sorting ${a.name} after ${b.name} due to dy=${dy}. dx was ${dx}`);
////                    }
//                    return dy;
//                }
////                if (dx < 0) {
////                    console.log(`sorting ${a.name} before ${b.name} due to dx=${dx}`);
////                }
////                else if (dx > 0) {
////                    console.log(`sorting ${a.name} after ${b.name} due to dx=${dx}`);
////                }
//                return dx; // otherwise sort them left to right
//            }
//            return -1;
//        }
//        if (b.position) {
//            return 1;
//        }
//        return 0; // neither has a position
//    });
//    return sorted_nodes;
//}

ShortCoursePlayStore.prototype.getActions = function() {
    return this.actions;
};

ShortCoursePlayStore.prototype.getStore = function() {
    return this.store;
};

ShortCoursePlayStore.prototype.getDoc = function() {
    return this.doc;
};

ShortCoursePlayStore.prototype.getFeaturePermissions = function() {
    if (this.doc.featurePermissions) {
        return this.doc.featurePermissions;
    }
    if (this.getIsPreview()) {
        console.log("allowing all featurePermissions for preview."); // TODO: return org account_features here
        return {
            useVoiceSynthesis: true,
            useVoiceSynthesisElevenLabs: true,
            useVoiceSynthesisCartesia: true,
            useChatBot: true,
        };
    }
    return {};
}

ShortCoursePlayStore.prototype.getChannelId = function() {
    return this.channelId || null;
}

ShortCoursePlayStore.prototype.getShortCourseId = function() {
    return this.shortCourseId;; 
}

ShortCoursePlayStore.prototype.getPerfectScore = function() {
    return this.doc.perfect_score; // 85; 
}

ShortCoursePlayStore.prototype.getErrorTitle = function() {
    return this.errorInfo.title;
};

ShortCoursePlayStore.prototype.getErrorText = function() {
    return this.errorInfo.text;
};

// add data for preview to mirror structure when playing a course instance
ShortCoursePlayStore.prototype.initPreviewInstance = function() {
	// set up default user subskill states
	_.forEach(this.doc.subskills,(ss)=>{
		_.forEach(ss.node_relations,(nr,node_id)=>{
			if (!nr.subskill_states) {
				nr.subskill_states = {};
				nr.subskill_states[this.user_id] = {
					gate: 1,
					attempts: 0,
					score: null,
					remediating: false,
                    remediation_level: 0,
				};
			}
		});
	});
};

ShortCoursePlayStore.prototype.iterateAttempts = function(fnVisit,includeIncomplete) {
    if (this.getIsPreview()) {
        _.forEach(this.previewContentBlockAttempts,(entry)=>{
            let cb = _.find(this.doc.content_blocks,{hash: entry.hash});
            let activity_name;
            if (cb) {
                if (cb.activity && cb.activity.meta_data && cb.activity.meta_data) {
                    let m = cb.activity.meta_data || {};
                    if (m.display_name) {
                        activity_name = m.display_name;
                    }
                    else if (m.title) {
                        activity_name = m.title;
                    }
                }
                if (!activity_name) {
                    let activity = _.find(this.activities,{activity_id: cb.nova_activity_id});
                    if (activity && activity.meta_data && activity.meta_data) {
                        let m = activity.meta_data || {};
                        if (m.display_name) {
                            activity_name = m.display_name;
                        }
                        else if (m.title) {
                            activity_name = m.title;
                        }
                    }
                }
            }
            if (!activity_name) {
                activity_name = `Activity for nova_activity_id ${cb.nova_activity_id}`; // fallback
            }

            fnVisit({
                activity_name: activity_name,
                content_block: cb,
                node_id: entry.node_id,
                attempt_time: entry.attempt_time,
                // score_data: entry.scoreData,
                activityTypeObj: entry.activityTypeObj,
                skillsInvolved: entry.skillsInvolved,
                subskillImpacts: entry.subskillImpacts,
                fail_count: entry.fail_count, 
                remedial: entry.remedial,
                passed_gate: entry.passed_gate,
                
            });
            
        });
        return;
    }
    
    const doc = this.doc;
    _.forEach(doc.content_blocks,(cb)=>{
        const isBot = (cb.type === Globals.short_course.content_block_type.CHATBOT);
        let activity_name;
        if (isBot) {
            activity_name = cb.display_title;
        }
        else {
            activity_name = `Activity for nova_activity_id ${cb.nova_activity_id}`; // fallback
            if (cb && cb.activity && cb.activity.meta_data && cb.activity.meta_data) {
                let m = cb.activity.meta_data;
                if (m.display_name) {
                    activity_name = m.display_name;
                }
                else if (m.title) {
                    activity_name = m.title;
                }
            }
        }
        _.forEach(cb.content_block_instances,(cbi)=>{
            if (!isBot) {
                if (!cbi.sc_analytics || !cbi.sc_analytics.scoreCategoryTotalsByUser) {
                    console.log(`missing sc_analytics scoreCategoryTotalsByUser for cbi`,cbi);
                    return;
                }
            }
            _.forEach(cbi.attempts,(attempt)=>{
                if (cbi.users_to_attempts) {
                    let u2a;
                    if (isBot) {
                        u2a = _.find(cbi.users_to_attempts,{attempt: attempt.attempt, user_id: String(this.user_id)});
                    }
                    else {
                        u2a = _.find(cbi.users_to_attempts,{content_attempt_id: attempt.id, user_id: String(this.user_id)}); // why is it a string sometimes and an integer other times...
                    }
                    if (!u2a) {
                        console.log(`attempt without user on ${activity_name}, content block ${cb.hash}`);
                        return;
                    }
                        
                    const timestamp = new Date(u2a.created_at);
                    let node_id = cbi.node_id;
                    let nr = cb.node_relations[cbi.node_id];
                    if (!node_id) {
                        console.log(`attempt without a node on ${activity_name}, content block ${cb.hash}`);
                        return;
                    }
                    
                    if (!includeIncomplete && (u2a.user_status_token !== 'COMPLETE')) {
                        // console.log(`ignoring attempt with status ${u2a.user_status_token} on ${activity_name}, content block ${cb.hash}`);
                        return;
                    }
                    
                    let scoresBySubskillHash,score,scoreData;
                    if (isBot) {
                        scoresBySubskillHash = {};
                        _.forEach(u2a.subskillImpacts,(entry)=>{
                            scoresBySubskillHash[entry.subskill_hash] = entry.score;
                        });
                    }
                    else {
                        score = u2a.status_score;
                        scoreData = { score, score_categories: [] };
                        let userEntry = cbi.sc_analytics.scoreCategoryTotalsByUser[this.user_hash];
                        if (!userEntry) {
                            // console.log(`cbi without userEntry for ${this.user_hash}\n${JSON.stringify(cbi)}`);
                            if (!includeIncomplete) {
                                return;
                            }
                            userEntry = {}; // dummy value
                        }
                        const attemptEntry = userEntry[attempt.nova_act_inst_id];
                        if (!attemptEntry) {
                            // console.log(`userEntry without attemptEntry for ${this.user_hash} ${attempt.nova_act_inst_id}`, cbi);
                        }
                            
                        // console.log(`activity ${activity_name}\n attemptEntry: ${JSON.stringify(attemptEntry)}\n attempt:${JSON.stringify(attempt)}`);
                        _.forEach(attemptEntry,(sc,entry_key)=>{
                            if (typeof sc.sc_name === 'undefined') {
                                if (entry_key !== 'flags') {
                                    console.log("unexpected scoreCategoryTotalsByUser entry",sc,entry_key);
                                }
                                return;
                            }
                            const name = sc.sc_name; // aka category_id
                            let display_name;
                            if (cbi.sc_analytics.scoreCategories) {
                                const cat = _.find(cbi.sc_analytics.scoreCategories,{name});
                                if (cat) {
                                    display_name = cat.display_name;
                                }
                            }
                            scoreData.score_categories.push({
                                category_id: name,
                                display_name, 
                                score: sc.score,
                                max_score: sc.max_score,
                            });
                        });
                    }
                    let activity_type = nr ? nr.activity_type : cb.activity_type;
                    let activityTypeObj = _.find(doc.activity_types,{id:activity_type});
                    if (activityTypeObj) {
                        let remedial = (activityTypeObj.credit_type === Globals.short_course.node_content_credit_type.PRACTICE);
                        let remediation_level = remedial ? Number(activityTypeObj.gate) : 0;
                        let fail_count = 0; 
                        let not_passed_count = 0;
                        let states_involved = []; // for update at the end if we passed all of them
                        let skillsInvolved = [];
                        let subskillImpacts = u2a.subskillImpacts;
                        _.forEach(u2a.subskillImpacts,(impact)=>{
                            let ss = _.find(doc.subskills,{hash: impact.subskill_hash});
                            let subskill_score = impact.score;
                            let passed_subskill = (subskill_score >= doc.mastery_score);
                            skillsInvolved.push({
                                subskill: ss,
                                score: impact.score,
                                passed: passed_subskill,
                                gate_impact: impact.gate_impact,
                                remedial_impact: impact.remedial_impact,
                            });
                        });

                        // see if there are any subskills which were not involved in this activity that we have yet to pass
                        _.forEach(doc.subskills,(ss)=>{
                            let nr = ss.node_relations[node_id];
                            if (nr) {
                                let user_subskill_state = ss.node_relations[node_id].subskill_states[this.user_id];
                                if (states_involved.indexOf(user_subskill_state) < 0) {
                                    if (user_subskill_state.remediating) {
                                        //console.log(`still need to pass ${ss.display_name}`);
                                        not_passed_count++;
                                    }
                                    states_involved.push(user_subskill_state); // also update other node skills if we pass the gate
                                }
                            }
                        });


                        let passed_gate;
                        if (states_involved.length && !fail_count && !not_passed_count) {
                            // passed the gate
                            passed_gate = true;
                            // console.log(`${node_id} gate ${activityTypeObj.gate} passed`);
                            //_.forEach(states_involved,(user_subskill_state)=>{
                            //    user_subskill_state.score = null;
                            //    user_subskill_state.attempts = 0;
                            //    user_subskill_state.gate = activityTypeObj.gate + 1;
                            //});
                        }
                    
                        fnVisit({
                            activity_name,
                            content_block: cb,
                            node_id,
                            attempt_time: timestamp,
                            scoreData, // nova activity
                            scoresBySubskillHash, // chatbot activity
                            activityTypeObj,
                            skillsInvolved,
                            subskillImpacts,
                            fail_count, 
                            remedial,
                            remediation_level,
                            passed_gate,
                        });
                    } // if we have a valid activityTypeObj
                } // if we have user to attempt data
                else {
                    console.log(`no user attempts of ${activity_name}`);
                }
            }); // for attempts of a particular content block instance
        }); // for content block instances
    }); // for content blocks
	
}; // iterateAttempts


// look at course instance attempts and determine what node and content block user was looking at last (redmine #5535 guess)
ShortCoursePlayStore.prototype.processAttempts = function() {
//    let content_block_statuses = [];
    let latest_d;
    let content_block_hash = null; 
    let node_id = null;
    let attempt_count = 0;
    _.forEach(this.doc.content_blocks,(cb)=>{
        
        // FIXME: remove unnecessary activity_name
        let activity_name = `Activity for nova_activity_id ${cb.nova_activity_id}`; // fallback
        if (cb && cb.activity && cb.activity.meta_data && cb.activity.meta_data) {
            let m = cb.activity.meta_data;
            if (m.display_name) {
                activity_name = m.display_name;
            }
            else if (m.title) {
                activity_name = m.title;
            }
        }
        // FIXME: end debug code
        
        // let attempt_map = {};
        // process attempts if we have them
        _.forEach(cb.content_block_instances,(cbi)=>{
            _.forEach(cbi.attempts,(attempt)=>{
                if (cbi.users_to_attempts) {
                    let u2a = _.find(cbi.users_to_attempts,{content_attempt_id: attempt.id, user_id: String(this.user_id)}); // why is it a string sometimes and an integer other times...
                    if (u2a) {
                        attempt_count++;
                        
                        // pick a node_id for recommendation engine
                        let d = new Date(u2a.created_at);
                        if (!latest_d || (d>latest_d)) {
                            //if (latest_d) {
                            //    console.log(`${latest_d} is later than ${d.toString()}`);
                            //}
                            let found_node_id;
                            _.forEach(cb.node_relations,(nr,_node_id)=>{
                                found_node_id = _node_id;
                                return false; // pick the first node_id (arbitrarily).  If there is more than one node for a content block our guess can be wrong.
                            });
                            if (found_node_id) {
                                content_block_hash = cb.hash;
                                node_id = found_node_id;
                                latest_d = d;
                            }
                            else {
                                console.log(`attempt without a node in content block ${cb.hash}`);
                            }
                        }
                        
//                        if (u2a.user_status_token === 'COMPLETE') {
//                            const score = u2a.status_score;
//                            let score_data = { score, score_categories: [] };
//                            if (cbi.sc_analytics && cbi.sc_analytics.scoreCategoryTotalsByUser) {
//                                const userEntry = cbi.sc_analytics.scoreCategoryTotalsByUser[this.user_hash];
//                                if (userEntry) {
//                                    const attemptEntry = userEntry[attempt.nova_act_inst_id];
//                                    if (attemptEntry) {
//                                        // console.log(`activity ${activity_name}\n attemptEntry: ${JSON.stringify(attemptEntry)}\n attempt:${JSON.stringify(attempt)}`);
//                                        _.forEach(attemptEntry,(sc,entry_key)=>{
//                                            if (typeof sc.sc_name === 'undefined') {
//                                                if (entry_key !== 'flags') {
//                                                    console.log("unexpected scoreCategoryTotalsByUser entry",sc,entry_key);
//                                                }
//                                                return;
//                                            }
//                                            const name = sc.sc_name; // aka category_id
//                                            let display_name;
//                                            if (cbi.sc_analytics.scoreCategories) {
//                                                const cat = _.find(cbi.sc_analytics.scoreCategories,{name});
//                                                if (cat) {
//                                                    display_name = cat.display_name;
//                                                }
//                                            }
//                                            score_data.score_categories.push({
//                                                category_id: name,
//                                                display_name, 
//                                                score: sc.score,
//                                                max_score: sc.max_score,
//                                            });
//                                        });
//                                    }
//                                    else {
//                                        console.log(`userEntry without attemptEntry for ${this.user_hash} ${attempt.nova_act_inst_id}`, cbi); // FIXME: remove debug spam
//                                    }
//                                    
//                                }
//                                else {
//                                    console.log(`cbi without userEntry for ${this.user_hash}\n${JSON.stringify(cbi)}`); // FIXME: remove debug spam
//                                }
//                            }
//                            if (typeof attempt_map[cbi.node_id] === 'undefined') {
//                                attempt_map[cbi.node_id] = [];
//                            }
//                            attempt_map[cbi.node_id].push(score_data);
//                        }
                    } // if we found our user
                } // if we have user to attempt data
                else {
                    // console.log(`no user attempts of ${activity_name}`);
                }
            }); // for attempts of a particular content block instance
        }); // for content block instances
		
//        _.forEach(attempt_map,(attempts,node_id)=>{
//            content_block_statuses.push({hash: cb.hash, node_id, attempts});
//        });
    }); // for content blocks
	
    return { 
		// content_block_statuses,  
		node_id, 
		content_block_hash,
        attempt_count
	};
};

ShortCoursePlayStore.prototype.checkToUpdateDoc = function(possiblyChangedDoc) {
    let anyRelevantChanges = false;
	if (!_.isEqual(this.doc.subskills,possiblyChangedDoc.subskills)) {
		anyRelevantChanges = true;
	}
	if (!anyRelevantChanges) {
		_.forEach(this.doc.content_blocks,(cb)=>{
			let other_cb = _.find(possiblyChangedDoc.content_blocks,{hash: cb.hash});
			if (!other_cb) {
				anyRelevantChanges = true;
				return false;
			}
			if (other_cb.content_block_instances) {
				if (!cb.content_block_instances) {
					anyRelevantChanges = true;
					return false;
				}
				_.forEach(other_cb.content_block_instances,(other_cbi)=>{
					if (other_cbi.attempts) {
						let cbi = _.find(cb.content_block_instances,{node_id:other_cbi.node_id});
						if (!cbi || !_.isEqual(cbi.attempts, other_cbi.attempts)) {
							anyRelevantChanges = true;
							return false;
						}
					}
				});
			}
		});
	}
	
    if (!anyRelevantChanges) {
        return;
    }
    let convertedDoc = ShortCourseEditorStore.applyConversions(possiblyChangedDoc);
    this.doc = convertedDoc;

    setTimeout(()=>{
        this.actions.refreshDoc();
    },1);
};

ShortCoursePlayStore.prototype.getArbitraryIncompleteNode = function(latest_attempt_node_id, optional_cb_hash) {
    return ShortCourseUtil.getArbitraryIncompleteNode(this.doc,latest_attempt_node_id, optional_cb_hash);
};

ShortCoursePlayStore.prototype.startPlay = function() {
    return this.actions.startPlay();
};

ShortCoursePlayStore.FLOW_FOCI = FLOW_FOCI;
ShortCoursePlayStore.REPORT_CARD_PHASE = REPORT_CARD_PHASE;

ShortCoursePlayStore.prototype.getNodeType = function(node) {
	if (!node) {
		console.trace("getNodeType called without node");
		return;
	}
    return ShortCourseEditorStore.determineNodeType(node);
}

ShortCoursePlayStore.prototype.isSkillNode = function(node) {
    return !node.capstone;
}

ShortCoursePlayStore.prototype.isCompletionNode = function(node) {
    return node.capstone;
}

ShortCoursePlayStore.prototype.getFlowFocus = function() {
    return this.store.getState().flowFocus;
}

// for comment system
ShortCoursePlayStore.prototype.getCurrentNavLocation = function() {
    let ss = this.store.getState();
//    ShortCourseConstants.NAV_TYPES: {
//        Settings: 'settings',
//        Node: 'node',
//        CoachScript: 'coach_script',
//        RichText: 'rich_text', // for common rich text entries that can be used by different coach actions
//        Comment: 'comments',
//    },

    const doc = this.getDoc();
    let coach_script_id = this.getCoachScriptId();
    let action_index = this.getCoachActionIndex();
    const coachScriptEntry = _.find(doc.client_content.coach_scripts,{id:coach_script_id});
    const actionEntry = coachScriptEntry && coachScriptEntry.actions[action_index];
    let rich_text_id = actionEntry && actionEntry.rich_text_id;

    let loc = {
        type: ss.node_id ? ShortCourseConstants.NAV_TYPES.Node : null, 
        id: ss.node_id,
        coach_script_id,
        action_index,
        rich_text_id,
    };
    switch(loc.type) {
        case FLOW_FOCI.ActivityAttempt:
        case FLOW_FOCI.ContentBlockLink: // TODO: any changes for this?
        case FLOW_FOCI.ChatBotAttempt:
            loc.content_block_hash = this.currentAttempt && this.currentAttempt.content_block_hash;
    }
    return loc;
}

ShortCoursePlayStore.prototype.getIsPreview = function() {
    return this.startParams.preview ? true : false
}

// like debugLog but more targeted (preview mode only)
ShortCoursePlayStore.prototype.diagLog = function() {
    if ((GlobalConfig.DebugMode || GlobalConfig.DevMode) && this.getIsPreview()) {
        console.log.apply(console,arguments);
    }
}

ShortCoursePlayStore.prototype.setAvatar = function(avatar) {
    let avatar_name = avatar.name;
    let avatar_hash = avatar.hash;
    if (avatar_name) {
        this.doc.user.avatar_name = avatar_name;
    }
    if (avatar_hash) {
        this.doc.user.avatar_hash = avatar_hash;
    }
}

ShortCoursePlayStore.prototype.getAvatar = function() {
    let user = this.doc.user;
    return {
        name: user.avatar_name || user.first_name,
        hash: user.avatar_hash,
    }
//    let avatar = (user && user.avatar) || {}
//    if (!avatar.name && user && user.first_name) {
//        avatar.name = user.first_name;
//    }
//    return avatar;
}

ShortCoursePlayStore.prototype.getUserPersistentData = function() {
    let user = this.doc.user;
    return user.persistant_data || {};
}

ShortCoursePlayStore.prototype.getPersistentFlag = function(flagName) {
    let persistentData = this.getUserPersistentData();
    let shortCourseFlags = persistentData.shortCourseFlags || {};
    return shortCourseFlags[flagName];
}

// called after avatar has been updated (global avatar associated with user)
// Here we make sure our copy of the user has the updated data. 
ShortCoursePlayStore.prototype.onUpdateAvatar = function({avatar_name,avatar_hash}) {
    let user = this.doc.user;
    user.avatar_name = avatar_name;
    user.avatar_hash = avatar_hash;
}

ShortCoursePlayStore.prototype.hasAvatar = function() {
    let avatar = this.getAvatar();
    if (avatar && avatar.hash) {
        return true;
    }
    return false;
}

ShortCoursePlayStore.prototype.hasVoice = function() {
    const doc = this.doc;
    if (doc.client_content.voiceOptions && 
        doc.client_content.voiceOptions.engine && 
        doc.client_content.voiceOptions.voice_id) {
        return true;
    }
    return false;
}

ShortCoursePlayStore.prototype.hasWelcomeScreen = function() {
    const doc = this.getDoc();
    const introScriptEntry = doc && doc.client_content && _.find(doc.client_content.coach_scripts,{id:COACH_SCRIPT_ID.INTRO});
    return (introScriptEntry && introScriptEntry.actions.length) ? true : false;
}


ShortCoursePlayStore.prototype.getNodeId = function() {
    return this.store.getState().node_id;
}

ShortCoursePlayStore.prototype.getContentBlock = function(hash) {
    return hash && _.find(this.doc.content_blocks,{hash});
}

ShortCoursePlayStore.prototype.getCurrentContentBlockHash = function() {
    return this.store.getState().content_block_hash;
};

ShortCoursePlayStore.prototype.getCurrentContentBlock = function() {
    return this.getContentBlock(this.getCurrentContentBlockHash());
}

// return attempts for a particular content block
//ShortCoursePlayStore.prototype.getContentBlockAttempts = function(hash,node_id) {
//    if (!hash || !node_id) {
//        console.log(`getContentBlockAttempts missing arg(s)`);
//        return [];
//    }
//    const storeState = this.store.getState();
//    const stats = _.find(storeState.content_block_statuses,{hash,node_id});
//    if (!stats) {
//        // console.log(`no stats for content_block ${hash} and node_id ${node_id}`);
//        return [];
//    }
//    return stats.attempts;
//}

// extract subskill states for node (and current user) from instance doc
ShortCoursePlayStore.prototype.getNodeSubskillStates = function(node_id) {
	let subskills = [];
    if (!node_id) {
        return subskills;
    }
	_.forEach(this.doc.subskills,(ss)=>{
		let nr = ss.node_relations[node_id];
		if (nr) {
			let ss_state = nr.subskill_states[this.user_id];
			if (ss_state) {
				subskills.push({
					hash: ss.hash,
					display_name: ss.display_name,
                    description: ss.description,
					...ss_state
				});
			}
		}
	})
	return subskills;
};

// returns { gate, min_gate, max_gate, remediating, remediation_level }  
ShortCoursePlayStore.prototype.getNodeGateStatus = function(node_id) {
    return ShortCourseUtil.getNodeGateStatus(this.doc,node_id);
}

// static function
// returns parameters to append to play url (incluing '?')
ShortCoursePlayStore.getStaticShortCoursePlayParams = function(options={}) {
    let params = FeatureParams.buildActivityLaunchSearchParams(options);
    if (params) {
        return '?' + params;
    }
    return '';
}

ShortCoursePlayStore.prototype.getShortCoursePlayParams = function() {
    let avatar = this.getAvatar() || {};
    let options = {
        startingHelpScreen: false,
        introScreen: false,
        endFeedback: false,
    };
    if (avatar) {
        if (avatar.name) {
            options.avatarName = avatar.name;
        }
        if (avatar.hash) {
            options.avatarHash = avatar.hash;
        }
    }
    return ShortCoursePlayStore.getStaticShortCoursePlayParams(options);
}

ShortCoursePlayStore.prototype.getCurrentContentBlockUrl = function() {
    if (!this.getIsPreview()) {
        console.log("not supported"); // for preview only
        return;
    }

    const cb = this.getCurrentContentBlock();
    if (!cb) {
        console.log(`no content block specified`);
        return null;
    }
    switch(cb.type) {
        case Globals.short_course.content_block_type.ACTIVITY: {
            let activity = _.find(this.activities,{activity_id: cb.nova_activity_id});
            if (!activity) {
                console.log(`activity ${cb.nova_activity_id} not found`);
                return null;
            }
            let template = this.templates[activity.template_id];
            if (!template) {
                console.log(`template ${activity.template_id} not found`);
                return null;
            }
            let params = this.getShortCoursePlayParams();
            return `${template.template_url}/preview/web/${cb.nova_activity_id}/${cb.activity_version}${params}`
        }
        case Globals.short_course.content_block_type.LINK:
            // TODO
            break;
    }
    console.log(`no url for content block type ${cb.type}`);
    return null;
}

ShortCoursePlayStore.prototype.getContentBlockTitle = function(hash) {
    const contentBlock = this.getContentBlock(hash);
    if (!contentBlock) {
        return;
    }
    
    switch(contentBlock.type) {
        case Globals.short_course.content_block_type.ACTIVITY:
        {
            if (!this.getIsPreview()) {
                return contentBlock.activity.meta_data.display_name || contentBlock.activity.meta_data.title;
            }
            let activity = _.find(this.activities,{activity_id: contentBlock.nova_activity_id});
            if (!activity) {
                console.log(`activity ${contentBlock.nova_activity_id} not found`);
                return 'content block with unknown activity';
            }
            if (activity.meta_data && activity.meta_data.display_name) {
                return activity.meta_data.display_name;
            }
            // console.log("getContentBlockTitle does not have meta_data.display_name. Using title instead"); // fallback
            return activity.title;
        }
        case Globals.short_course.content_block_type.LINK:
            return contentBlock.link_display_name;
        case Globals.short_course.content_block_type.CHATBOT:
            return contentBlock.display_title;
        default:
        
    }
    console.log(`unable to determine title for content block ${contentBlock.hash}`);
    return 'Content Block';
};

ShortCoursePlayStore.prototype.getCurrentContentBlockTitle = function() {
    return this.getContentBlockTitle(this.getCurrentContentBlockHash());
}

// return the attempts for the current content block
//ShortCoursePlayStore.prototype.getCurrentAttempts = function() {
//    const cb = this.getCurrentContentBlock();
//    if (!cb) {
//        return;
//    }
//    let node_id = this.store.getState().node_id;
//    return this.getContentBlockAttempts(cb.hash,node_id);
//}

// return true if  the specified node is itself complete (not checking prerequisites per-se)
ShortCoursePlayStore.prototype.isNodeComplete = function(node_id) {
    return ShortCourseUtil.isNodeComplete(this.doc,node_id);
}


ShortCoursePlayStore.prototype.getNodesInOrder = function() {
    return this.nodes_in_order;
}

//ShortCoursePlayStore.prototype.nodesSortedLeftToRight = function() {
//    return this.left_to_right_sorted_nodes;
//}

// recursive.  
// returns true if all the prerequisites are satisfied or false if any are not
ShortCoursePlayStore.prototype.getArePrerequisitesSatisfied = function(node_id,visited) {
    return ShortCourseUtil.getArePrerequisitesSatisfied(this.doc,node_id,visited);
};

// gets information on the last attempt the student made given a particular node_id
ShortCoursePlayStore.prototype.getLastActivityAttempt = function(specific_node_id) {
    let latestAttemptData = null; // latest remedial or assessment attempt
    let latestAssessmentAttemptData = null;
    let latestSubskillScores = [];
    let latestTutorialAttemptData = null;
    let attemptsSummary = {
        assessmentsPassed: 0,
        attemptsWithSubskillGateAdvances: 0,
        totalSubskillGateAdvances: 0,
    };
    //if (!specific_node_id) {
    //    return { latestAttemptData, latestAssessmentAttemptData, latestSubskillScores, latestTutorialAttemptData, attemptsSummary };
    //}
    const doc = this.getDoc();
    this.iterateAttempts((attemptData)=>{
        if (!specific_node_id || (specific_node_id === attemptData.node_id)) {
            let node_id = attemptData.node_id;
            let isTutorial = (attemptData.activityTypeObj && (attemptData.activityTypeObj.credit_type === Globals.short_course.node_content_credit_type.TUTORIAL));
            
            // aggregate latest scores
            let cb = attemptData.content_block;
            if (cb) {
                const attempt_time = attemptData.attempt_time;
                _.forEach(attemptData.skillsInvolved,(entry)=>{
                    const { subskill, score, passed } = entry;
                    const hash = subskill.hash;
                    // verify that the skillsInvolved entry is in the doc
                    let ss = _.find(doc.subskills,{hash});
                    if (ss && ss.node_relations[node_id]) {
                        let x_entry = _.find(latestSubskillScores,{hash});
                        if (!x_entry) {
                            // console.log(`found ${ss.display_name} score ${score}`);
                            latestSubskillScores.push({hash, attempt_time, score});
                        }
                        else if (attempt_time > x_entry.attempt_time) {
                            // console.log(`replacing ${ss.display_name} score ${x_entry.score} with later score ${score}`);
                            x_entry.attempt_time = attempt_time;
                            x_entry.score = score;
                        }
                    }
                });
            }

            if (isTutorial) {
                if (!latestTutorialAttemptData) {
                    latestTutorialAttemptData = attemptData;
                }
                else if (attemptData.attempt_time > latestTutorialAttemptData.attempt_time) {
                    latestTutorialAttemptData = attemptData;
                }
            }
            else {
                if (attemptData.subskillImpacts) {
                    let positiveGateImpacts = _.filter(attemptData.subskillImpacts,(impact)=>(impact.gate_impact > 0));
                    if (positiveGateImpacts.length) {
                        attemptsSummary.attemptsWithSubskillGateAdvances++;
                        attemptsSummary.totalSubskillGateAdvances += positiveGateImpacts.length;
                    }
                }
                
                
                if (!latestAttemptData) {
                    latestAttemptData = attemptData;
                }
                else if (attemptData.attempt_time > latestAttemptData.attempt_time) {
                    latestAttemptData = attemptData;
                }
                if (attemptData.activityTypeObj && (attemptData.activityTypeObj.credit_type === Globals.short_course.node_content_credit_type.ASSESSMENT)) {
                    if (!latestAssessmentAttemptData) {
                        latestAssessmentAttemptData = attemptData;
                    }
                    else if (attemptData.attempt_time > latestAssessmentAttemptData.attempt_time) {
                        latestAssessmentAttemptData = attemptData;
                    }
                    if (attemptData.passed_gate) {
                        attemptsSummary.assessmentsPassed++;
                    }
                }
            }
        }
    });
    return { latestAttemptData, latestAssessmentAttemptData, latestSubskillScores, latestTutorialAttemptData, attemptsSummary };
};

ShortCoursePlayStore.prototype.testConditions = function(conditions, coachConditions) {
    if (!conditions) {
        return true; // no conditions: passed
    }
    if (typeof coachConditions === 'undefined') {
        coachConditions = this.getStore().getState().coachConditions;
    }
    let passedConditions = true;
    _.forEach(conditions,(c,i)=>{
        if (coachConditions[c.type] !== c.value) {
            //console.log(`${c.type} is ${coachConditions[c.type]}. conditions failed after testing [${i}].`);
            passedConditions = false;
            return false;
        }
        //else {
            //console.log(`${c.type} is ${coachConditions[c.type]}. condition[${i}] ok.`);
        //}
    });
    return passedConditions;
}

ShortCoursePlayStore.prototype.testActionConditions = function(actionEntry, coachConditions) {
    return this.testConditions(actionEntry.conditions,coachConditions);
}


ShortCoursePlayStore.prototype.getFirstCoachScriptOffset = function(coach_script_id, coachConditions) {
    let coachScript = _.find(this.doc.client_content.coach_scripts,{id:coach_script_id});
    if (!coachScript) {
        console.log(`getFirstCoachScriptOffset: coachScript ${coach_script_id} does not exist`);
        return 0;
    }
    let L = coachScript.actions.length;
    for (var i=0; i<L; ++i) {
        let test = coachScript.actions[i];
        if (test) {
            let passedConditions = this.testActionConditions(test, coachConditions);
            if (passedConditions) {
                return i;
            }
        }
    }
    console.log(`getFirstCoachScriptOffset: coachScript ${coach_script_id} has no valid actions`);
    return 0; // no valid actions
}


ShortCoursePlayStore.prototype.getNextCoachScriptOffset=function(coach_script_id,currentOffset) {
    let coachScript = _.find(this.doc.client_content.coach_scripts,{id:coach_script_id});
    if (!coachScript) {
        return 0;
    }
    const coachConditions = this.getStore().getState().coachConditions;
    let test;
    do {
        currentOffset++;
        test = coachScript.actions[currentOffset];
        if (test) {
            let passedConditions = this.testActionConditions(test, coachConditions);
            if (passedConditions) {
                return currentOffset;
            }
            //console.log(`getNextCoachScriptOffset for ${coach_script_id} action ${currentOffset} conditions failed`);
        }
    } while(test);
    return 0; // no more actions
};

ShortCoursePlayStore.prototype.getRichText = function(richTextQuery) {
    const doc = this.getDoc();
    const richTextEntry = _.find(doc.client_content.rich_texts,richTextQuery);
    if (richTextEntry) {
        return richTextEntry.rich_text;
    }
    // return undefined
}

ShortCoursePlayStore.prototype.getStickerSrc = function(stickerQuery) {
    const doc = this.getDoc();
    let stickerEntry = _.find(doc.client_content.coach_stickers,stickerQuery);
    if (stickerEntry) {
        return stickerEntry.src;
    }
}

ShortCoursePlayStore.prototype.getTutorialContentBlock = function(node_id) {
    if (!node_id) {
        return false;
    }
    const doc = this.getDoc();
    let tut_cb = null;
    _.forEach(doc.content_blocks,(cb,index)=>{
        if (cb.type === Globals.short_course.content_block_type.ACTIVITY) {
            let nr = cb.node_relations[node_id];
            if (!nr) {
                return;
            }
            let activity_type = nr ? nr.activity_type : cb.activity_type; // cb.activity_type exists with preview data but not course instance data
            if (!activity_type) {
                return;
            }
            let activityTypeObj = _.find(doc.activity_types,{id:activity_type});
            if (activityTypeObj && (activityTypeObj.credit_type === Globals.short_course.node_content_credit_type.TUTORIAL)) {
                tut_cb = cb; // done searching
                return false;
            }
        }
    });
    return tut_cb;
} // getTutorialContentBlock

// return coach_script_id, coachVariables based on current state
ShortCoursePlayStore.prototype.calculateCoachVariables = function() {
    let storeState = this.getStore().getState();
    let flowFocus = storeState.flowFocus;
    let node_id = storeState.node_id;
    let { latestAttemptData:attemptData, latestAssessmentAttemptData, latestSubskillScores, latestTutorialAttemptData, attemptsSummary } = this.getLastActivityAttempt(/*node_id*/);
    const tut_cb = this.getTutorialContentBlock(node_id);
    
    let coach_script_id;
    let coachVariables = {};

    let { coach_script_id: coach_script_id_for_next_activity, coachVariables:someCoachVariables, coachConditions, remedial_skills } = ( node_id ? this.gatherCoachDataForNode(node_id) : {} );
    
    coachVariables = {...coachVariables, ...someCoachVariables };
    
    let coach_script_id_for_last_attempt; // coach_script_id if we are reviewing the last attempt 
    let justFinishedTutorial = false;

    if (attemptData) {
        let pass_count = 0;
        let fail_count = 0;
        let perfect_count = 0;
        const perfect_score = this.getPerfectScore(); // score value considered to be 'perfect'
        _.forEach(attemptData.skillsInvolved,(si)=>{
            if (si.passed) {
                pass_count++;
                if (si.score >= perfect_score) {
                    perfect_count++;
                }
            }
            else {
                fail_count++;
            }
        });

        if (attemptData.remedial) {
            coach_script_id_for_last_attempt = attemptData.fail_count ?  COACH_SCRIPT_ID.ATTEMPT_COMPLETE_REMEDIATION_FAILED : COACH_SCRIPT_ID.ATTEMPT_COMPLETE_REMEDIATION_PASSED;
        }
        else {
            if (attemptData.passed_gate) {
                if (perfect_count === pass_count) {
                    coach_script_id_for_last_attempt = COACH_SCRIPT_ID.ATTEMPT_COMPLETE_ASSESSMENT_PERFECT;
                }
                else {
                    coach_script_id_for_last_attempt = COACH_SCRIPT_ID.ATTEMPT_COMPLETE_ASSESSMENT_PASSED;
                }
            }
            else {
                if (pass_count) {
                    coach_script_id_for_last_attempt = COACH_SCRIPT_ID.ATTEMPT_COMPLETE_ASSESSMENT_NEEDS_WORK;
                }
                else {
                    coach_script_id_for_last_attempt = COACH_SCRIPT_ID.ATTEMPT_COMPLETE_ASSESSMENT_FAILED;
                }
            }
        }
        if (!coach_script_id_for_last_attempt) {
            console.log("failed to determine coach_script_id_for_last_attempt",attemptData); // FIXME: remove debug spam
        }
    }
    else { //  no non-tutorial attempts
        if (latestTutorialAttemptData) {
            coach_script_id_for_last_attempt = COACH_SCRIPT_ID.NO_ATTEMPTS;
            justFinishedTutorial = true;
        }
        else if (tut_cb) {
            coach_script_id_for_next_activity = COACH_SCRIPT_ID.TUTORIAL;
        }
        else {
            coach_script_id_for_next_activity = COACH_SCRIPT_ID.NO_ATTEMPTS;
        }
    }

    switch(flowFocus) {
        case FLOW_FOCI.WelcomeScreen:
        case FLOW_FOCI.AvatarSelectScreen:
            coach_script_id = COACH_SCRIPT_ID.INTRO;
            break;
            
        case FLOW_FOCI.CourseCompleted:
            coach_script_id = COACH_SCRIPT_ID.COURSE_COMPLETE;
            break;
        
        case FLOW_FOCI.AttemptCompleted: // we just completed an activity
            coach_script_id = coach_script_id_for_last_attempt;
            break;

        case FLOW_FOCI.SingleNode: // an activity has been selected 
            coach_script_id = coach_script_id_for_next_activity;
            break;
    }
    
    if (!coach_script_id) {
        console.log("calculateCoachVariables without coach_script_id. storeState:",JSON.stringify(storeState));
    }
    return { coach_script_id, coachVariables, coachConditions, remedial_skills, justFinishedTutorial };
} // calculateCoachVariables


ShortCoursePlayStore.prototype.getCoachScriptId = function() {
    let storeState = this.getStore().getState();
    return storeState.coach_script_id;
}

ShortCoursePlayStore.prototype.getCoachActionIndex = function() {
    let storeState = this.getStore().getState();
    return storeState.coach_script_action_offset;
}

ShortCoursePlayStore.prototype.getValidCoachActionIndex = function() {
    const doc = this.getDoc();
    const storeState = this.getStore().getState();
    const coachScriptEntry = _.find(doc.client_content.coach_scripts,{id:storeState.coach_script_id});
    if (!coachScriptEntry) {
        return -1;
    }
    return Math.min(Math.max(0,storeState.coach_script_action_offset), coachScriptEntry.actions.length-1);
}

ShortCoursePlayStore.prototype.getCoachVariables = function() {
    return this.getStore().getState().coachVariables;
}

ShortCoursePlayStore.prototype.getViableSnippets = function(phraseSetEntry,coachVariables) {
    let viableSnippets = [];
    _.forEach(phraseSetEntry.phrases,(phrase)=>{
        if (phrase) {
            viableSnippets.push(phrase);
        }
        else {
            let isViable = true;
            _.forEach(coachVariables,(v,k)=>{
                if (!v) {
                    let vk = `{${k}}`;
                    if (phrase.indexOf(vk)>=0) { // phrase uses blank variable, which means it is NOT viable
                        isViable = false; 
                        return false;
                    }
                }
            });
            if (isViable) {
                viableSnippets.push(phrase);
            }
        }
    });
    return viableSnippets;
} // getViableSnippets

ShortCoursePlayStore.prototype.lookupPhrase = function(rollParams,coachVariables,phraseToken,phraseSetEntry) {
    let phrase;
    let passedConditions = this.testConditions(phraseSetEntry.conditions);
    if (!passedConditions) {
        return ""; // blank phrase if conditions did not pass
    }
    let viableSnippets = this.getViableSnippets(phraseSetEntry,coachVariables);
    if (viableSnippets.length > 1) {
        let r = this.lookupPhraseRoll(rollParams, phraseToken) % viableSnippets.length;
        phrase = viableSnippets[r];
    }
    else if (viableSnippets.length === 1) {
        phrase = viableSnippets[0]; // no choices
    }
    else {
        console.log(`${phraseToken} had no viable phrases.  Will be blank`); // FIXME: remove debug spam
        phrase = ""; // blank phrase
    }
    return phrase;
} // lookupPhrase


ShortCoursePlayStore.prototype.makeVariableSubstitutions = function(richText,coachVariables,rollParams) {
    const doc = this.getDoc();
    const tokenPrefix =`{${ShortCourseConstants.PHRASE_VARIABLE_PREFIX}`;
    let anyPhrasesUsed = (richText.indexOf(tokenPrefix) >= 0);
    if (anyPhrasesUsed && doc.client_content.phraseData) {
        _.forEach(doc.client_content.phraseData.phraseSets,(entry)=>{
            let phraseToken =`{${ShortCourseConstants.PHRASE_VARIABLE_PREFIX + entry.variableSuffix}}`;
            if (richText.indexOf(phraseToken) >= 0) {
                // this phrase is used in the richText
                let phrase = this.lookupPhrase(rollParams,coachVariables,phraseToken,entry);
                richText = richText.split(phraseToken).join(phrase);
            }
        });
    }

    _.forEach(coachVariables,(v,k)=>{
        if (typeof v !== 'undefined') {
            richText = richText
            .split(`{${k}}`).join(v);
        }
    });
    return richText;

} // makeVariableSubstitutions


ShortCoursePlayStore.prototype.getLastAttemptData = function() {
    let storeState = this.getStore().getState();
    let node_id = storeState.node_id;
    let { latestAttemptData:attemptData, latestAssessmentAttemptData, latestSubskillScores, attemptsSummary } =  this.getLastActivityAttempt(/*node_id*/);
    let remedial_skills = storeState.coach_remedial_skills;
    return { attemptData, latestAssessmentAttemptData, remedial_skills, attemptsSummary };

}

ShortCoursePlayStore.prototype.getCoachAction = function(coach_script_id, action_index) {
    const doc = this.getDoc();
    if (typeof action_index === 'undefined') {
        action_index = this.getCoachActionIndex();
    }
    let coachScriptEntry = _.find(doc.client_content.coach_scripts,{id:coach_script_id});
    if (!coachScriptEntry) {
        return;
    }
    return coachScriptEntry.actions[action_index];
}

ShortCoursePlayStore.prototype.canAdvanceCoachScript = function() {
    let state = this.getStore().getState();
    let action_index = state.coach_script_action_offset;
    let next_index = this.getNextCoachScriptOffset(state.coach_script_id, action_index);
    return (next_index > 0) ? true : false;
}

ShortCoursePlayStore.prototype.canAdvanceReportCard = function() {
    let storeState = this.getStore().getState();
    const staticScriptEntry = _.find(ShortCourseConstants.COACH_SCRIPTS,{id:storeState.coach_script_id});
    if (!staticScriptEntry.showCriteriaScores) {
        return false;
    }
    let {attemptData, latestAssessmentAttemptData, remedial_skills} = this.getLastAttemptData();
    if (!attemptData || !attemptData.skillsInvolved || !attemptData.skillsInvolved.length) {
        return false;
    }
    let phase = storeState.coach_script_report_card_phase;
    if (phase < REPORT_CARD_PHASE.END) {
        return true;
    }
    return false;
}

ShortCoursePlayStore.prototype.getReportCardPhase = function() {
    let storeState = this.getStore().getState();
    return storeState.coach_script_report_card_phase;
}

ShortCoursePlayStore.prototype.getReportCardPhaseRichText = function() {
    let phase = this.getReportCardPhase();
    return this.getRichText({reportCard:true, phase});
}

ShortCoursePlayStore.prototype.getReportCardActivePhase = function() {
    let coach_script_id = this.getCoachScriptId();
    const staticScriptEntry = _.find(ShortCourseConstants.COACH_SCRIPTS,{id:coach_script_id});
    if (staticScriptEntry && staticScriptEntry.showCriteriaScores) {
        return this.getReportCardPhase();
    }
    return null; 
}

ShortCoursePlayStore.prototype.getCoachActionDisplayElements = function(coach_script_id, action_index) {
    let richText,stickerSrc;
    if (typeof action_index === 'undefined') {
        action_index = this.getCoachActionIndex();
    }
    let actionEntry = this.getCoachAction(coach_script_id, action_index);
    if (actionEntry) {
        let coachConditions = this.getStore().getState().coachConditions;
        // console.log(`getCoachActionDisplayElements testing conditions for action[${action_index}] in ${coach_script_id}`);
        let passedConditions = this.testActionConditions(actionEntry, coachConditions);
        if (passedConditions) {
            richText = this.getRichText({id:actionEntry.rich_text_id});
            stickerSrc = this.getStickerSrc({id:actionEntry.coach_sticker_id});
        }
        else {
            actionEntry = undefined; // indicate conditions failed
        }
    }
    return { actionEntry, richText, stickerSrc };
}

ShortCoursePlayStore.prototype.getPriorCoachVariables = function() {
    let ss = this.store.getState();
    if (ss && ss.priorCoachState) {
        return ss.priorCoachState.coachVariables;
    }
}

ShortCoursePlayStore.prototype.getPriorCoachScriptId = function() {
    let ss = this.store.getState();
    if (!ss || !ss.priorCoachState) {
        return;
    }
    return ss.priorCoachState.coach_script_id;
}

ShortCoursePlayStore.prototype.getPriorCoachActionDisplayElements = function() {
    let ss = this.store.getState();
    if (!ss || !ss.priorCoachState) {
        return;
    }
    const doc = this.getDoc();
    const coachScriptEntry = _.find(doc.client_content.coach_scripts,{id:ss.priorCoachState.coach_script_id});
    if (!coachScriptEntry) {
        return;
    }
    const coachConditions = ss.priorCoachState.coachConditions;
    let prior_elements = [];
    for (var action_index=0; action_index < coachScriptEntry.actions.length; ++action_index) {
        let richText,stickerSrc;
        let actionEntry = coachScriptEntry.actions[action_index];
        if (actionEntry) {
            let passedConditions = this.testActionConditions(actionEntry, coachConditions);
            if (passedConditions) {
                richText = this.getRichText({id:actionEntry.rich_text_id});
                stickerSrc = this.getStickerSrc({id:actionEntry.coach_sticker_id});
            }
            else {
                actionEntry = undefined; // indicates conditions failed
            }
            prior_elements[action_index] = { actionEntry, richText, stickerSrc };
        }
    }
    return prior_elements;
}

// private helper function
// updates and returns coachVariables, coachConditions
ShortCoursePlayStore.prototype._getCoachDataForLastAttempt=function({latestAttemptData, latestAssessmentAttemptData, latestSubskillScores, attemptsSummary}) {
    const doc = this.getDoc();
    const storeState = this.store.getState();

    // calculate worstSkill (not considering next recommended activity)
    let worstSkill;
    if (latestAttemptData) {
        _.forEach(latestAttemptData.skillsInvolved,(sk)=>{
            if (!sk.passed) {
                if (!worstSkill) {
                    worstSkill = sk;
                }
                else if (worstSkill.score > sk.score) {
                    worstSkill = sk;
                }
            }
        });
    }

    // substitutions:
    // {previousActivityName} - the name of the previous activity (could be remedial or assessment)
    // {failedSkillName} - the name of a subskill that we just failed
    // {lastAssessment} - the name of the previous assessment activity 
    
    let previousActivityName = latestAttemptData ? latestAttemptData.activity_name : "";
    let failedSkillName = worstSkill ? worstSkill.subskill.display_name : "";
    let lastAssessment = latestAssessmentAttemptData ? latestAssessmentAttemptData.activity_name : "";


    // additional coach variables for redmine #6182
    // {checksEarned} - the number of checks earned since the last gate
    // {checksLeft} - the number of checks needed for the next gate
    // {strongSkills} - A list of skills on which the student scored above the “Perfect Score” threshold.
    //                 These should be given in descending order.
    //{weakSkills} - A list of skills on which the student scored below the “Mastery Score” threshold.
    //                 These should be given in ascending order.
    
    // raw data (will be formatted after)
    let strongSkills = [];
    let weakSkills = [];
    
    const node_id = latestAttemptData ? latestAttemptData.node_id : storeState.node_id;
    const perfect_score = this.getPerfectScore(); // score value considered to be 'perfect'
    let num_node_subskills = 0;
    let num_node_subskill_scores = 0;
    let nodeGateStatus;
    if (node_id) {
        nodeGateStatus =this.getNodeGateStatus(node_id);
    }
    else {
        //console.log("using default nodeGateStatus because we don't have a node_id");
        nodeGateStatus = {
            gate: 1,
            min_gate: 1,
            max_gate: 4,
            remediating: false,
            remediation_level:0,
            stars: 0, 
            total_stars: 0
        };
    }
    // {gate, min_gate, max_gate, remediating, remediation_level}
    let over_gate_count = 0;
    let at_gate_count = 0;
    let under_gate_count = 0;
    let remediating_count = 0;
    
    if (node_id) {
        // count gates
        _.forEach(doc.subskills,(ss,subskill_id)=>{
            let nr = ss.node_relations[node_id];
            if (nr) {
                num_node_subskills++;
                let user_subskill_state = nr.subskill_states[this.user_id];
                if (user_subskill_state) {
                    if (user_subskill_state.remediating) {
                        remediating_count++;
                    }
                    if (user_subskill_state.gate > nodeGateStatus.gate) {
                        over_gate_count++;
                    }
                    else if (user_subskill_state.gate === nodeGateStatus.gate) {
                        at_gate_count++;
                    }
                    else {
                        under_gate_count++;
                    }
                }
            }

            // evaluate scores
            if (latestSubskillScores) {
                let scoreEntry = _.find(latestSubskillScores,{hash:ss.hash});
                if (scoreEntry) {
                    let passed_subskill = (scoreEntry.score >= doc.mastery_score);
                    if (!passed_subskill) {
                        // console.log(`subskill ${ss.display_name} score indicates not passed (weak)`);
                        weakSkills.push({...ss, ...scoreEntry});
                    }
                    else {
                        let perfect_subskill = (scoreEntry.score >= perfect_score);
                        if (perfect_subskill) {
                            // console.log(`subskill ${ss.display_name} score indicates perfect (strong)`);
                            strongSkills.push({...ss,...scoreEntry});
                        }
                        //else {
                            // console.log(`subskill ${ss.display_name} score indicates pass but not perfect`);
                        //}
                    }
                }
            }
        });
        // count scores vs the last assessment
    }

    let checksEarned, checksLeft;
    if (!remediating_count && latestAttemptData && latestAttemptData.passed_gate) {
        checksEarned = over_gate_count + at_gate_count; // should also be num_node_subskills
        checksLeft = under_gate_count; // should also be 0
    }
    else {
        checksEarned = over_gate_count;
        checksLeft = at_gate_count + under_gate_count;
    }
    
    checksEarned=String(checksEarned);
    checksLeft = String(num_node_subskills - checksEarned);

    // sort and convert array variables to text so they can be substituted into rich_text string
    const formatCoachSkillList=(arr)=>{ // TBD
        let L = arr ? arr.length : 0;
        if (L<1) {
            return '';
        }
        let result_string = arr[0].display_name;
        let i;
        for (i=1; i < L; ++i) {
            const name = arr[i].display_name;
            if (i < L-1) { // if not the last element
                result_string += `, ${name}`;
            }
            else {
                if (L === 2) { // if there are exactly two elements
                    result_string += ` and ${name}`;
                }
                else {
                    result_string += `, and ${name}`; // oxford comma (╭ರ_•́)
                }
            }
        }
        return result_string;
    }
    
    const isInvolved=(someSkill)=>(
        _.find(latestAttemptData.skillsInvolved,(si)=>(si.subskill.hash === someSkill.hash)) ? true : false
    )

    strongSkills = _.sortBy(strongSkills,[(entry)=>(-entry.score)]); // sort from highest to lowest
    weakSkills = _.sortBy(weakSkills,['score']); // sort from lowest to highest

    let nodeStrongSkills = strongSkills.slice();
    strongSkills = _.filter(strongSkills,isInvolved);
    
    let nodeWeakSkills = weakSkills.slice();
    weakSkills = _.filter(weakSkills,isInvolved);

    // conditions
    let firstCheck = false;
    let firstStar = false;
    let firstCheckEver = false;
    let firstStarEver = false;
    let noAdvancesNoChecks = true;
    let advancedNoChecks = false;
    let oneOrMoreChecks = false;
    
    if (latestAssessmentAttemptData && latestAssessmentAttemptData.passed_gate) {
        if (attemptsSummary.assessmentsPassed === 1) {
            firstStar = true;
            let flag = this.getPersistentFlag('firstStar');
            if (!flag || (flag === this.doc.hash)) {
                firstStarEver = true;
            }
        }
    }

    let passedSkills = [];
    let failedSkills = [];
    if (latestAttemptData) {
        let anyChecks = false;
        _.forEach(latestAttemptData.skillsInvolved,(entry)=>{
            if (entry.gate_impact > 0) {
                anyChecks = true;
                noAdvancesNoChecks = false;
                oneOrMoreChecks = true;
            }
            else if (entry.remedial_impact > 0) {
                advancedNoChecks = true;
            }
            if (entry.passed) {
                passedSkills.push({...entry.subskill, score: entry.score});
            }
            else {
                failedSkills.push({...entry.subskill, score: entry.score});
            }
        });
        passedSkills = _.sortBy(passedSkills,[(entry)=>(-entry.score)]); // sort from highest to lowest
        failedSkills = _.sortBy(failedSkills,['score']); // sort from lowest to highest
        if ((attemptsSummary.attemptsWithSubskillGateAdvances === 1) && anyChecks) {
            firstCheck = true;
            let flag = this.getPersistentFlag('firstCheck');
            if (!flag || (flag === this.doc.hash)) {
                firstCheckEver = true;
            }
        }
    }
    if (oneOrMoreChecks) {
        advancedNoChecks = false;
    }
    
    let nodeRemediatingSkillName,nodeRemediatingSkillDescription;
    if (nodeWeakSkills && nodeWeakSkills[0]) {
        let w = nodeWeakSkills[0];
        nodeRemediatingSkillName = w.display_name;
        nodeRemediatingSkillDescription = w.description;
    }
    

    nodeStrongSkills = formatCoachSkillList(nodeStrongSkills); // may include subskills not involved in last activity
    nodeWeakSkills = formatCoachSkillList(nodeWeakSkills); // may include subskills not involved in last activity
    strongSkills = formatCoachSkillList(strongSkills);
    weakSkills = formatCoachSkillList(weakSkills);
    passedSkills = formatCoachSkillList(passedSkills); // Note: This is used in phase advancement logic
    failedSkills = formatCoachSkillList(failedSkills); // Note: This is used in phase advancement logic

    let coachVariables = { 
        previousActivityName, failedSkillName, lastAssessment,
        checksEarned, checksLeft, strongSkills, weakSkills, 
        passedSkills, failedSkills, nodeStrongSkills, nodeWeakSkills,
        nodeRemediatingSkillName,nodeRemediatingSkillDescription, // fallbacks for remediatingSkillName, remediatingSkillDescription
    };
    let coachConditions = {
        firstCheck, firstStar, firstCheckEver, firstStarEver, noAdvancesNoChecks, advancedNoChecks, oneOrMoreChecks,
    };
  
    return {worstSkill, coachVariables, coachConditions};
} // _getCoachDataForLastAttempt

// private helper function
// updates coachVariables, returns coach_script_id
ShortCoursePlayStore.prototype._updateCoachVariablesForRecommendation=function({cb_info, latestAttemptData, coachVariables}) {
    const doc = this.getDoc();
    let cb = cb_info && _.find(doc.content_blocks,{hash: cb_info.hash});

    let coach_script_id;
    if (doc.user.course_complete) {
        coach_script_id = COACH_SCRIPT_ID.COURSE_COMPLETE;
    }
    else if (!latestAttemptData) {
        coach_script_id = COACH_SCRIPT_ID.NO_ATTEMPTS;
    }
    else if (!latestAttemptData.remedial) {
        if (latestAttemptData.passed_gate) {
            coach_script_id = COACH_SCRIPT_ID.ASSESSMENT_PASSED;
        }
        else {
            coach_script_id = COACH_SCRIPT_ID.ASSESSMENT_NOT_PASSED;
        }
    }
    else {
        // last attempt was remedial
        if (!cb_info.remedial) { // next attempt is not remedial
            coach_script_id = COACH_SCRIPT_ID.REMEDIAL_ALL_PASSED;
        }
        else if (!latestAttemptData.fail_count) { // last remedial attempt succeeded but there is more remediation to do
            coach_script_id = COACH_SCRIPT_ID.REMEDIAL_SOME_PASSED;
        }
        else if (cb_info.hash === latestAttemptData.content_block.hash) { // we're trying the same remediation again
            coach_script_id = COACH_SCRIPT_ID.REMEDIAL_NOT_PASSED_TRY_AGAIN;
        }
        else {
            coach_script_id = COACH_SCRIPT_ID.REMEDIAL_NOT_PASSED_TRY_OTHER;
        }
    }
    if (!coach_script_id) {
        console.log("unable to determine coach_script_id");
    }

    let worstSkill;
    let worst_isRelated;
    if (cb && latestAttemptData) {
        _.forEach(latestAttemptData.skillsInvolved,(sk)=>{
            let sk_isRelated = cb.subskill_relations[sk.subskill.hash] ? true : false;
            if (!sk.passed) {
                if (!worstSkill) {
                    worstSkill = sk;
                    worst_isRelated = sk_isRelated;
                }
                else {
                    if (sk_isRelated && !worst_isRelated) {
                        // prefer a subskill that is related to the next activity
                        worstSkill = sk;
                        worst_isRelated = sk_isRelated;
                    }
                    else if ((sk_isRelated === worst_isRelated) && (worstSkill.score > sk.score)) {
                        // prefer a subskill with a worse score if both are equally related to the next activity
                        worstSkill = sk;
                        worst_isRelated = sk_isRelated;
                    }
                }
            }
        });
    }

    // coachVariables specifically related to recommendation
    coachVariables.nextActivityName = cb_info.name;
    coachVariables.failedSkillName = worstSkill ? worstSkill.subskill.display_name : "";
    return coach_script_id;
} // _updateCoachVariablesForRecommendation

const SAMPLE_DIFFICULTY = 'Easy'; 	// TODO: determine difficulty (or is it entered somewhere?)
const SAMPLE_EST_TIME = '8 min'; 	// TODO: get estimated time from analytics

// gets information on the next content block given a node_id that we just played and an optional selected content block and activities
ShortCoursePlayStore.prototype.gatherCoachDataForNode = function(node_id,selected_cb_hash,activities) {
    const doc = this.getDoc();
    const node = _.find(doc.nodes,{node_id});
    let content_block_info = [];
    let next_cb_info; // will be the recommended next content block activity
    let possible_next_cbs = []; // all possible next activities. These will get sorted with recommendation (worst skill) first
    let subskills = this.getNodeSubskillStates(node_id);
    let coach_script_id;
    
    let { latestAttemptData, latestAssessmentAttemptData, latestSubskillScores, latestTutorialAttemptData, attemptsSummary } = this.getLastActivityAttempt(/*node_id*/);
//    if (!latestAttemptData) {
//        console.log("gatherCoachDataForNode did not result in latestAttemptData"); // FIXME: remove debug spam
//        // insert breakpoint here
//        let test = this.getLastActivityAttempt(); // FIXME: remove debug code
//    }
    let { worstSkill, coachVariables, coachConditions} = this._getCoachDataForLastAttempt({latestAttemptData, latestAssessmentAttemptData, latestSubskillScores, attemptsSummary});

    if (!latestTutorialAttemptData && !latestAttemptData) {
        let cb = this.getTutorialContentBlock(node_id);
        if (cb) {
            let nr = cb.node_relations[node_id];
            let activity_type = nr ? nr.activity_type : cb.activity_type; // cb.activity_type exists with preview data but not course instance data
            if (!activity_type) {
                return;
            }
            let name = this.getContentBlockTitle(cb.hash);
            let atype = _.find(doc.activity_types,{id: activity_type});
            let activity_gate;
            if (atype) {
                activity_gate = atype.gate;
            }
            let img_src,description;
            //let template_icon = 'SmartChat.svg'; // default for preview until we have activity data for the editor
            let activity = cb.activity;
            if (!activity && activities) {
                activity = _.find(activities,{activity_id: cb.nova_activity_id})
            }
            if (activity) {
                if (activity.meta_data) {
                    img_src = activity.meta_data.image_url;
                    if (img_src) {
                        img_src = AssetUtil.resolveAssetPath(img_src);
                    }
                    description = activity.meta_data.display_name;
                }
                //if (activity.template) {
                //    template_icon = activity.template.icon;
                //}
            }
            let info = {
                hash: cb.hash,
                type: cb.type,
                name,
                img_src,
                description,
                // template_icon, // not used
                difficulty: SAMPLE_DIFFICULTY,
                est_time: SAMPLE_EST_TIME,
                gate: activity_gate,
                subskill_matches:0, 
                remediation_level_matches:0, 
                worst_subskill_match:0, 
                worst_remedial_match: 0,
                remedial: false, 
                remedial_level: 0,
                available: true,
                complete: false,
            };
            coach_script_id = this._updateCoachVariablesForRecommendation({cb_info:info, latestAttemptData, coachVariables});
            return {
                next_cb_info:info,
                possible_next_cbs: [info], 
                coach_script_id,
                coachVariables, coachConditions, 
                remedial_skills: undefined,
            };
        } // if we are recommending the tutorial
    } // if we haven't played anything (not even tutorial)
    
    let mastery_score = doc.mastery_score;
    if (typeof mastery_score === 'undefined') {
        // does the server provide mastery_score when returning the course instance?
        console.log("mastery_score not defined by doc. using default"); // FIXME: remove debug spam
        mastery_score = 75;
    }
    
    let { gate:node_gate, min_gate:node_min_gate, max_gate: node_max_gate, remediating, remediation_level }  = this.getNodeGateStatus(node_id);
    if ((node_gate < node_min_gate) && !remediating) {
        console.log(`adjusting node_gate ${node_gate} to node_min_gate ${node_min_gate}`); // FIXME: remove debug spam
        node_gate = node_min_gate;
    }
    
    let selected_cb_info;

    // determine worst subskill (for selecting best remediation activity)
    let worst_score = 100; // worst_score 
    let lowest_remedial_level = 3;
    let worst_score_lowest_remedial = 100; // worst score at lowest remediation level 
    _.forEach(doc.content_blocks,(cb,index)=>{
        if (cb.type !== Globals.short_course.content_block_type.ACTIVITY) {
            return;
        }
        _.forEach(cb.subskill_relations,(entry,hash)=>{
            let ss_state = _.find(subskills,{hash});
            if (ss_state) {
                if (ss_state.remediating) {
                    if (ss_state.score < worst_score) {
                        worst_score = ss_state.score;
                    }
                    if (ss_state.remediation_level > lowest_remedial_level) {
                        return;
                    }
                    if (ss_state.remediation_level < lowest_remedial_level) {
                        lowest_remedial_level = ss_state.remediation_level;
                    }
                    if (ss_state.remediation_level === lowest_remedial_level) {
                        if (ss_state.score < worst_score_lowest_remedial) {
                            worst_score_lowest_remedial = ss_state.score;
                        }
                    }
                }
            }
        });
    });

    let locked_cb;
    if (!remediating) {
        locked_cb = this.getLockedCapstoneContentBlock(node,node_gate);
        if (locked_cb) {
            coachVariables.nextActivityName = locked_cb.name;
        }
    }
    
    this.diagLog(`gatherCoachDataForNode building content_block_info for node_id ${node_id}. remediating=${remediating}`);
    _.forEach(doc.content_blocks,(cb,index)=>{
        let isBot = (cb.type === Globals.short_course.content_block_type.CHATBOT);
        let isNova = (cb.type === Globals.short_course.content_block_type.ACTIVITY);
        
        if (isNova || isBot) {
            let nr = cb.node_relations[node_id];
            if (!nr && remediating) {
                nr = cb.node_relations[null]; // Note: null entry exists for course instance data but not for preview data
            }
            let name = this.getContentBlockTitle(cb.hash);
            // this.diagLog(`Examining doc.content_blocks[${index}] isNova: ${isNova}, isBot: ${isBot} - ${name}`);

            if (!nr) {
                this.diagLog(`not associated with current node:  ${name} hash ${cb.hash}`);
                return;
            }

            let activity_type = nr ? nr.activity_type : cb.activity_type; // cb.activity_type exists with preview data but not course instance data
            if (!activity_type) {
                this.diagLog(`${cb.hash} ${name} didn't have an activity type`);
                return;
            }
            if (remediating) {
                if (Globals.short_course.REMEDIAL_ACTIVITY_TYPES.indexOf(activity_type) < 0) {
                    this.diagLog(`${cb.hash} ${name} type ${activity_type} is not remedial`);
                    return;
                }
            }
            else {
                if (Globals.short_course.ASSESSMENT_ACTIVITY_TYPES.indexOf(activity_type) < 0) {
                    this.diagLog(`${cb.hash} ${name} type ${activity_type} is not assessment`);
                    return;
                }
            }
            
            let atype = _.find(doc.activity_types,{id: activity_type});
            this.diagLog(`processing content block ${cb.hash} ${JSON.stringify(atype)} ${name} for node_id ${node_id}`);

            let activity_gate, remedial, remedial_level;
            let available = false;
            let complete = false;

            // only relevant if remediating
            let subskill_matches = 0; 
            let remediation_level_matches = 0; 
            let worst_subskill_match = false;
            let worst_remedial_match = false;
            if (atype) {
                activity_gate = atype.gate;
                remedial = (atype.credit_type === Globals.short_course.node_content_credit_type.PRACTICE);
                remedial_level = remedial ? atype.gate : 0;
                if (remediating) {
                    // see if any of it's subskills are relevant 
                    _.forEach(cb.subskill_relations,(entry,hash)=>{
                        let ss_state = _.find(subskills,{hash});
                        if (ss_state) {
                            if (ss_state.remediating) {
                                if (worst_score == ss_state.score) {
                                    worst_subskill_match = true;
                                }
                                subskill_matches++;
                                if (lowest_remedial_level === atype.gate) {
                                    remediation_level_matches++;
                                    if (worst_score_lowest_remedial == ss_state.score) {
                                        worst_remedial_match = true;
                                    }
                                }
                            }
                        }
                    });
                    available = remedial && (activity_gate <= node_gate) && subskill_matches;
                    //console.log(`${cb.hash} ${name} available=${available}`);
                    //console.log(`${name} ${activity_type}: remedial=${remedial}, activity_gate: ${activity_gate}, node_gate: ${node_gate}, remediation_level_matches: ${remediation_level_matches}, subskill_matches: ${subskill_matches}`);
                } 
                else {
                    available = !remedial && (activity_gate === node_gate);
                    //console.log(`${cb.hash} ${name} available=${available}`);
                    complete = ( node_gate > activity_gate );
                }
            }
             
            let img_src,description;
            if (isNova) {
                // let template_icon = 'SmartChat.svg'; // default for preview until we have activity data for the editor
                let activity = cb.activity;
                if (!activity && activities) {
                    activity = _.find(activities,{activity_id: cb.nova_activity_id})
                }
                if (activity) {
                    if (activity.meta_data) {
                        img_src = activity.meta_data.image_url;
                        if (img_src) {
                            img_src = AssetUtil.resolveAssetPath(img_src);
                        }
                        description = activity.meta_data.display_name;
                    }
                    //if (activity.template) {
                    //    template_icon = activity.template.icon;
                    //}
                }
            }
            else if (isBot) {
                img_src = AssetUtil.resolveAssetPath(cb.bot_options.image_url || '');
                description = cb.bot_options.description; 
            }
            
            if (available && (cb.hash === selected_cb_hash)) {
                selected_cb_info = info;
            }
            
            content_block_info.push({
                hash: cb.hash,
                type: cb.type,
                name,img_src,description,
                // template_icon, // not used
                difficulty: SAMPLE_DIFFICULTY,
                est_time: SAMPLE_EST_TIME,
                gate: activity_gate,
                subskill_matches, remediation_level_matches, worst_subskill_match, worst_remedial_match, // used for picking next activity
                remedial, 
                remedial_level,
                available,
                complete,
            });
        }
    });
    
    content_block_info.sort((a,b)=>(a.gate - b.gate));
    
    this.diagLog("Going through content_block_info to find possible next activities");
    _.forEach(content_block_info,(info,key)=>{
        this.diagLog(`[${key}]: available: ${info.available ? 'true ':'false'}, complete: ${info.complete}, name: ${info.name}`); // FIXME: show the activity name here and why it was or wasn't filtered out here
        if (info.available && !info.complete) {
            possible_next_cbs.push(info);
        }
    });
    
    possible_next_cbs.sort((a,b)=>{
        //console.log(`comparing a ${a.name} to b ${b.name}`);
        if (a.worst_remedial_match && !b.worst_remedial_match) {
            //console.log(`true: a.worst_remedial_match {${a.worst_remedial_match}} && !b.worst_remedial_match {${b.worst_remedial_match}}`);
            return -1;
        }
        if (b.worst_remedial_match  && !a.worst_remedial_match) {
            //console.log(`true: b.worst_remedial_match {${b.worst_remedial_match}} && !a.worst_remedial_match {${a.worst_remedial_match}}`);
            return 1;
        }
        
        if (b.remediation_level_matches < a.remediation_level_matches) {
            //console.log(`true: b.remediation_level_matches {${b.remediation_level_matches}} < a.remediation_level_matches {${a.remediation_level_matches}}`);
            return -1;
        }
        if (a.remediation_level_matches < b.remediation_level_matches) {
            //console.log(`true: a.remediation_level_matches {${a.remediation_level_matches}} < b.remediation_level_matches {${b.remediation_level_matches}}`);
            return 1;
        }

        
        if (a.worst_subskill_match && !b.worst_subskill_match) {
            //console.log(`true: a.worst_subskill_match {${a.worst_subskill_match}} && !b.worst_subskill_match {${b.worst_subskill_match}}`);
            return -1;
        }
        if (b.worst_subskill_match && !a.worst_subskill_match) {
            //console.log(`true: b.worst_subskill_match {${b.worst_subskill_match}} && !a.worst_subskill_match {${a.worst_subskill_match}}`);
            return 1;
        }


        if (b.subskill_matches < a.subskill_matches) {
            //console.log(`true: b.subskill_matches {${b.subskill_matches}} < a.subskill_matches {${a.subskill_matches}}`);
            return -1;
        }
        if (a.subskill_matches < b.subskill_matches) {
            //console.log(`true: a.subskill_matches {${a.subskill_matches}} < b.subskill_matches {${b.subskill_matches}}`);
            return 1;
        }

        if (latestAttemptData && (latestAttemptData.content_block.hash === a.hash)) {
            //console.log(`true: a was latestAttemptData`);
            return -1;
        }
        if (latestAttemptData && (latestAttemptData.content_block.hash === b.hash)) {
            //console.log(`true: b was latestAttemptData`);
            return 1;
        }

        return 0;
    });
    
    let non_bot_infos = [];
    let bot_infos = [];
    _.forEach(possible_next_cbs,(cb)=>{
        //let isBot = (cb.type === Globals.short_course.content_block_type.CHATBOT);
        // treat bot activities same as non-bot activities
        //if (isBot) {
        //    bot_infos.push(cb);
        //}
        //else {
            non_bot_infos.push(cb);
        //}
    });
    possible_next_cbs = non_bot_infos; // normal activities only
    
    if (locked_cb) {
        let info = _.find(possible_next_cbs,{hash:locked_cb.hash});
        if (info) {
            possible_next_cbs = [info]; // locked into earlier selection
        }
        else {
            console.log("Error: locked_cb not found in possible_next_cbs",possible_next_cbs,locked_cb);
        }
    }
    
    next_cb_info = possible_next_cbs[0]; // undefined if there were no available activities
    
    let info = selected_cb_info || next_cb_info || bot_infos[0];
    if (doc.user.course_complete) {
        let dummy_next_cb = {
            name: "no next activity: course is complete"
        };
        coach_script_id = this._updateCoachVariablesForRecommendation({cb_info:dummy_next_cb, latestAttemptData, coachVariables});
    }
    else if (info) {
        coach_script_id = this._updateCoachVariablesForRecommendation({cb_info:info, latestAttemptData, coachVariables});
    }
    else {
        console.log("no content blocks available?"); // FIXME: remove debug spam
        //console.log("subskills:",JSON.stringify(subskills)); // FIXME: remove debug spam
        //console.log("latestAttemptData:",JSON.stringify(latestAttemptData)); // FIXME: remove debug spam
        //console.log("doc:",JSON.stringify(doc)); // FIXME: remove debug spam
        coach_script_id = COACH_SCRIPT_ID.NO_NEXT_ACTIVITY;
    }

    let remedial_skills;
    if (latestAttemptData) {
        remedial_skills = _.filter(latestAttemptData.skillsInvolved,{passed:false});
        if (remedial_skills.length) {
            remedial_skills = _.sortBy(remedial_skills,['score']); // lowest score shown first
            
            // match worstSkill from gatherCoachDataForNode 
            if (worstSkill) {
                let worst_index = _.findIndex(remedial_skills,(rs)=>{
                    return (rs.subskill.hash === worstSkill.subskill.hash);
                });
                if (worst_index > 0) {
                    let temp = remedial_skills[0];
                    remedial_skills[0] = remedial_skills[worst_index];
                    remedial_skills[worst_index] = temp;
                }
            }
            coachVariables.remediatingSkillName = remedial_skills[0].subskill.display_name;
            coachVariables.remediatingSkillDescription = remedial_skills[0].subskill.description;
        }
        else {
            // fallback for REMEDIAL_SOME_PASSED situation in redmine #6309
            if (coachVariables.nodeRemediatingSkillName) {
                coachVariables.remediatingSkillName = coachVariables.nodeRemediatingSkillName;
                coachVariables.remediatingSkillDescription = coachVariables.nodeRemediatingSkillDescription;
            }
        }
    }

    // redmine #6258 cycle thru all possibilities 3 at a time
    // const top_recommendations = possible_next_cbs.slice(0,3); // up to 3 possible next activities (for redmine #6166) 

    return {
        next_cb_info,  // used by ShortCourseNode
        // top_recommendations
        possible_next_cbs, bot_infos,
        coach_script_id,  // used here
        coachVariables, coachConditions, // used here
        remedial_skills
    };
} // gatherCoachDataForNode

ShortCoursePlayStore.prototype.getLockedCapstoneContentBlock = function(node,node_gate) {
    let locked_cb = null;
    if (!node || !node.capstone) {
        return locked_cb; // none
    }
    const includeIncomplete = true; // locked as soon as they start the activity
    this.iterateAttempts((attemptData)=>{
        if (locked_cb) {
            return; // done
        }
        if (!attemptData.remedial && (attemptData.node_id === node.node_id)) {
            if (attemptData.activityTypeObj && 
               (attemptData.activityTypeObj.credit_type === Globals.short_course.node_content_credit_type.ASSESSMENT) && 
               (attemptData.activityTypeObj.gate === node_gate)) {
                locked_cb = attemptData.content_block;
            }
        }
    },includeIncomplete);
    if (locked_cb && locked_cb.remedial) {
        console.log("ERROR: getLockedCapstoneContentBlock called for remedial activity"); // FIXME: remove debug spam
        return null;
    }
    return locked_cb;
}

ShortCoursePlayStore.prototype.updateActivities = function(activities) {
    if (this.activities) {
        this.activities = activities;
        this.store.emitStatus();
    }
}

ShortCoursePlayStore.prototype.getInfoActivity = function(info) {
    const cb = _.find(this.doc.content_blocks,{hash: info.hash});
    if (!cb) {
        return null;
    }
    if (cb.activity) {
        return cb.activity;
    }
    if (this.activities) { // preview
        return _.find(this.activities,{activity_id:cb.nova_activity_id}); // TODO xxx
    }
};

ShortCoursePlayStore.prototype.getInfoMetaData = function(info) {
    let img_src = info.img_src;
    let description = info.description; // info.description is a duplicate of the activity name or title 
    let desc1,desc2; // additional descriptions
    let activity;
    const cb = _.find(this.doc.content_blocks,{hash: info.hash});
    if (!cb) {
        return { img_src, description, desc1, desc2 };
    }
    if (cb.activity && cb.activity.meta_data) { // available except when previewing
        description = cb.activity.meta_data.description || '';
        activity = cb.activity;
        // return { img_src, description };
    }
    else if (this.activities) { // preview
        activity = _.find(this.activities,{activity_id:cb.nova_activity_id}); // TODO xxx
        if (activity && activity.meta_data) {
            if (activity.meta_data.image_url) {
                img_src = activity.meta_data.image_url;
                img_src = AssetUtil.resolveAssetPath(img_src);
            }
            description = activity.meta_data.description || '';
        }
    }
    if (activity && activity.short_course_meta_data) {
        desc1 = activity.short_course_meta_data.desc1;
        desc2 = activity.short_course_meta_data.desc2;
    }
    return { img_src, description, desc1,desc2 };   
    // template icon: `${GlobalConfig.StaticUrl}/img/templates/${second_info.template_icon}`; // TODO
}

// break a string apart into sentences
function sentences(str,additionalSplitTokens,arr) {
    if (!arr) {
        arr = [];
    }
    str = str.replace(/\s\s*/g, ' ');  // replace 1 or more whitespace elements with single space
    let delim = str.search(/[\.!\?](?!\d)/); // possible ends of sentences (not followed by a digit)
    let delim_length = 1;
    let delim_token;
    if (additionalSplitTokens) {
        _.forEach(additionalSplitTokens,(token)=>{
            let d2 = str.indexOf(token);
            if (d2 >= 0) {
                if ((delim < 0) || (d2 < delim)) {
                    delim = d2;
                    delim_length = token.length;
                    delim_token = token;
                }
            }
        });
    }
    if (delim < 0) {
        let t = str.trim();
        if (t.length) {
            arr.push(t);
        }
        return arr;
    }
    let t = delim_token ? str.slice(0,delim).trim() : str.slice(0,delim+1).trim();
    if (t.length > 1) {
        arr.push(t);
    }
    if (delim_token) {
        arr.push(delim_token);
    }
    arr = sentences(str.slice(delim+delim_length),additionalSplitTokens,arr); // recurse
    return arr;
}

function nextCurlyBrace(str,offset) {
    let left = str.indexOf('{',offset);
    let right = str.indexOf('}',offset);
    if (left >= 0) {
        if (right >= 0) {
            if (left < right) {
                return { p: left, c:'{' };
            }
            return { p: right, c: '}'};
        }
        return { p: left, c:'{' };
    }
    if (right >= 0) {
        return { p: right, c: '}'};
    }
    return { p: -1, c: null }
}

// separate sentences.  Also split out dynamic portions in curly braces
ShortCoursePlayStore.prototype.generatePhraseData = function(str,phrases,splitOnVariables) {
    if (!phrases) {
        phrases = [];
    }
    
    let left_brace = str.indexOf('{');
    if (left_brace < 0) {
        // no braces at all.
        let arr = sentences(str);
        for (let i in arr) {
            phrases.push({phrase:arr[i]}); // nothing dynamic
        }
        return phrases;
    }

    const doc = this.doc;
    let additionalSplitTokens = [];
    if (doc.client_content.phraseData) {
        _.forEach(doc.client_content.phraseData.phraseSets,(entry)=>{
            let k =`{${ShortCourseConstants.PHRASE_VARIABLE_PREFIX + entry.variableSuffix}}`;
            if (str.indexOf(k) >= 0) {
                additionalSplitTokens.push(k);
            }
        })
    }
    
    if (!splitOnVariables) {
        // split into sentences, and mark the ones with braces as dynamic
        // but also split each phraseSet token into its own phrase
        let arr = sentences(str,additionalSplitTokens);
        for (let i in arr) {
            let phrase = arr[i];
            let dynamic = (phrase.indexOf('{') >= 0) ? true : false;
            phrases.push({phrase,dynamic});
        }
        return phrases;
    }
    
    let matching_right_brace = str.indexOf('}',left_brace+1);
    if (matching_right_brace < 0) {
        // there are no right braces to match the left brace we found.  Ignore the left brace.
        let arr = sentences(str);
        for (let i in arr) {
            phrases.push({phrase:arr[i]}); // nothing dynamic
        }
        return phrases;
    }
    
    let next_left_brace = str.indexOf('{',left_brace+1);
    if ((next_left_brace >= 0) && (next_left_brace < matching_right_brace)) {
        // we have some level of nesting
        let search_offset = next_left_brace + 1;
        let nest_level = 2;
        do {
            let {p,c} = nextCurlyBrace(str, search_offset);
            search_offset = p + 1;
            if (!c) { // nothing else we can iterate on. Give up and use the last matching right brace even if nesting doesn't match up
                break;
            }
            if (c === '{') {
                nest_level ++;
            }
            else {
                nest_level --;
                if (!nest_level) {
                    matching_right_brace = p;
                    break;
                }
            }
        } while(true);
    }
    
    // split str into three parts and recurse
    let str_a = str.slice(0,left_brace);
    let str_b = str.slice(left_brace,matching_right_brace+1);
    let str_c = str.slice(matching_right_brace+1);
    let arr = sentences(str_a);
    for (var i in arr) {
        phrases.push({phrase:arr[i]});
    }
    phrases.push({phrase:str_b, dynamic:true});
    this.generatePhraseData(str_c,phrases);
    return phrases;
} // generatePhraseData

ShortCoursePlayStore.prototype.onVoiceAudioPrepResult = function(prepParams,audio_src) {
    let {rich_text_id, index, phrase } = prepParams;
    let voiceData = this.richTextVoiceData[rich_text_id];
    if (voiceData) {
        let phraseEntry = voiceData[index];
        if (phraseEntry) {
            if (phrase !== phraseEntry.phrase) {
                console.log("onVoiceAudioPrepResult phrase mismatch",phraseEntry,prepParams);
            }
            else if (audio_src) {
                if (!phraseEntry.audio_src) {
                    //console.log("onVoiceAudioPrepResult filled in audio_src");
                    phraseEntry.audio_src = audio_src;
                }
                else {
                    //console.log("onVoiceAudioPrepResult after we already have an audio_src");
                }
            }
            else {
                if (!prepParams.retries) {
                    prepParams.retries = 1;
                }
                else {
                    prepParams.retries++;
                }
                
                if (prepParams.retries > 5) {
                    if (window.DEV || window.DEBUG) {
                        console.log("giving up on voice audio",prepParams);
                    }
                    return;
                }

                const { engine, voice_id } = this.doc.client_content.voiceOptions;
                const usageData = {
                    channelId: this.getChannelId(),
                    shortCourseId: this.getShortCourseId(),
                };
                
                let wait_ms = 5000 + prepParams.retries * 5000; // increasing delay before trying again (to avoid request bottleneck)
                // console.log(`onVoiceAudioPrepResult retry after ${wait_ms} ms on phrase ${phrase}`);
                let audio_promise = Promise.delay(wait_ms)
                .then(()=>{
                    return VoiceStore.synthesizeAudio({ engine, voice_id, phrase, usageData})
                })
                .then((audio_src)=>{
                    this.onVoiceAudioPrepResult(prepParams,audio_src);
                })
            }
        }
    }
}

// prepare for polly audio playback.  
// Any phrases without variables can go ahead and call the api to get the audio
// otherwise it will have to wait until the variables are known
ShortCoursePlayStore.prototype.prepVoiceAudio = Promise.method(function() {
    const { engine, voice_id } = this.doc.client_content.voiceOptions;
    const usageData = {
        channelId: this.getChannelId(),
        shortCourseId: this.getShortCourseId(),
    };
    
    let richTextVoiceData = {};
    let dummyDoc = document.implementation.createHTMLDocument('dummy'); // stops browser from preload images in the temporary div 
    let rtArray = this.doc.client_content.rich_texts;
    for (var i in rtArray) {
        const richTextEntry = rtArray[i];
        if (richTextEntry.rich_text) {
            let rtElem = $('<div/>',dummyDoc).html(richTextEntry.rich_text);
            let plainText = rtElem.text().trim();
            if (plainText) {
                let phraseData = this.generatePhraseData(plainText);
                let voiceDataArray = []
                for (var j in phraseData) {
                    let phraseEntry = phraseData[j];
                    if (phraseEntry.dynamic) {
                        voiceDataArray.push({ phrase: phraseEntry.phrase, audio_src: '', dynamic: true });
                    }
                    else {
                        let prepParams = {
                            rich_text_id: richTextEntry.id,
                            index: j,
                            phrase: phraseEntry.phrase, // sanity check
                        };
                        let audio_promise = VoiceStore.synthesizeAudio({ 
                            engine, 
                            voice_id, 
                            phrase: phraseEntry.phrase, 
                            usageData,
                        })
                        .then((audio_src)=>{
                            this.onVoiceAudioPrepResult(prepParams,audio_src);
                        })
                        voiceDataArray.push({ phrase: phraseEntry.phrase, audio_promise, dynamic: false }); // Note: audio_src is a Promise
                    }
                }
                richTextVoiceData[richTextEntry.id] = voiceDataArray;
            }
        }
    }
    this.richTextVoiceData = richTextVoiceData;
}) // prepVoiceAudio

ShortCoursePlayStore.prototype.updatePersistentVoiceOptions = function(voiceOptions) {
    const doc = this.doc;
    let client_state = doc.user.client_state || {};
    client_state.voiceOptions = voiceOptions;
    doc.user.client_state = client_state; // update local data
    if (!this.getIsPreview()) {
        ShortCourseInstanceActions.setShortCourseInstanceClientState(uuid.v4(),{instanceHash: doc.hash,client_state});
    }
};

ShortCoursePlayStore.prototype.updatePersistentClientState = function(storeState) {
    const doc = this.doc;
    let client_state = doc.user.client_state || {};
    let { coach_script_id, coachVariables, coachConditions, coach_script_action_offset, voiceOptions } = storeState;
    let allAttemptsCount = 0;
    if (this.getIsPreview()) {
        allAttemptsCount = this.previewContentBlockAttempts.length;
    }
    else {
        _.forEach(doc.content_blocks,(cb)=>{
            _.forEach(cb.content_block_instances,(cbi)=>{
                _.forEach(cbi.attempts,(attempt)=>{
                    let u2a = _.find(cbi.users_to_attempts,{content_attempt_id: attempt.id, user_id: String(this.user_id)});
                    if (u2a) {
                        allAttemptsCount++;
                    }
                });
            });
        });
    }
    client_state.allAttemptsCount = allAttemptsCount; // for convenience
    
    
    const coachScriptEntry = _.find(doc.client_content.coach_scripts,{id:coach_script_id});
    if (coachScriptEntry && doc.client_content.phraseData) {
        let rollQuery = { allAttemptsCount, coach_script_id };
        // establish which index to use for each phraseSets using allAttemptsCount and coach_script_id
        let currentPhraseRolls;
        if (!client_state.phraseRolls) {
            client_state.phraseRolls = [];
        }
        else {
            currentPhraseRolls = _.find(client_state.phraseRolls, rollQuery);
        }
        
        if (!currentPhraseRolls) {
            // see if any phrase sets are used
            let tokenRolls = [];
            const tokenPrefix =`{${ShortCourseConstants.PHRASE_VARIABLE_PREFIX}`;
            _.forEach(coachScriptEntry.actions,(action,action_index)=>{
                const richTextEntry = _.find(doc.client_content.rich_texts,{id:action.rich_text_id});
                if (!richTextEntry || !richTextEntry.rich_text) {
                    return;
                }
                const str = richTextEntry.rich_text;
                if (str.indexOf(tokenPrefix) >= 0) {
                    if (doc.client_content.phraseData) {
                        _.forEach(doc.client_content.phraseData.phraseSets,(entry)=>{
                            let token =`{${ShortCourseConstants.PHRASE_VARIABLE_PREFIX + entry.variableSuffix}}`;
                            if (str.indexOf(token) >= 0) {
                                tokenRolls.push({
                                    action_index,
                                    token,
                                    roll: Math.floor(Math.random() * 1000000)
                                });
                            }
                        })
                    }
                }
            });
            if (tokenRolls.length) {
                client_state.phraseRolls.push({
                    ...rollQuery,
                    tokenRolls
                });
                doc.user.client_state = client_state; // update local data
                if (!this.getIsPreview()) {
                    ShortCourseInstanceActions.setShortCourseInstanceClientState(uuid.v4(),{instanceHash: doc.hash,client_state});
                }
            }
        }
    }

    
    this.prepVoiceDynamicPhrases(coach_script_id, coachVariables, coachConditions, coach_script_action_offset);
    
    return client_state;
} // updatePersistentClientState

ShortCoursePlayStore.prototype.lookupPhraseRoll = function(rollParams,phraseToken) {
    let {coach_script_id,action_index} = rollParams;
    let client_state = this.doc.user.client_state || {};
    let allAttemptsCount = client_state.allAttemptsCount;
    let rollQuery = { allAttemptsCount, coach_script_id };
    let currentPhraseRolls = _.find(client_state.phraseRolls, rollQuery);
    if (currentPhraseRolls) {
        let tokenRoll = _.find(currentPhraseRolls.tokenRolls,{action_index,token:phraseToken});
        if (tokenRoll) {
            return tokenRoll.roll;
        }
    }
    console.log("lookupPhraseRoll did not find roll. chosen phrase may not be stable");
    return Math.floor(Math.random() * 1000000);
}

// queue voice synthesis for dynamic phrases related to the specified coach script
ShortCoursePlayStore.prototype.prepVoiceDynamicPhrases = Promise.method(function(coach_script_id, coachVariables, coachConditions, start_action_index) {
    if (!this.richTextVoiceData) {
        return null; // we didn't call prepVoiceAudio (presuming because it wasn't set up for voice)
    }
    let coachScript = _.find(this.doc.client_content.coach_scripts,{id:coach_script_id});
    if (!coachScript) {
        return null;
    }
    const doc = this.doc;
    const { engine, voice_id } = doc.client_content.voiceOptions;
    const usageData = {
        channelId: this.getChannelId(),
        shortCourseId: this.getShortCourseId(),
    };
    const tokenPrefix =`{${ShortCourseConstants.PHRASE_VARIABLE_PREFIX}`;
    
    var i = start_action_index;
    let rollParams = {coach_script_id,action_index:i};
    var actionEntry;
    do {
        actionEntry = coachScript.actions[i];
        if (actionEntry && actionEntry.rich_text_id) {
            let voiceData = this.richTextVoiceData[actionEntry.rich_text_id];
            if (voiceData) {
                let passedConditions = this.testActionConditions(actionEntry, coachConditions);
                if (passedConditions) {
                    for (var j=0; j<voiceData.length; ++j) {
                        let phraseEntry = voiceData[j];
                        if (phraseEntry.dynamic) {
                            let phrase = phraseEntry.phrase;

                            // check for phrase set
                            if (phrase.indexOf(tokenPrefix)>=0) {
                                let found = false;
                                if (doc.client_content.phraseData) {
                                    // replace phrase set variables with the correct permutation
                                    _.forEach(doc.client_content.phraseData.phraseSets,(entry)=>{
                                        let phraseToken =`{${ShortCourseConstants.PHRASE_VARIABLE_PREFIX + entry.variableSuffix}}`;
                                        if (phrase === phraseToken) { // should have been parsed to be on a line by itself
                                            phrase = this.lookupPhrase(rollParams,coachVariables,phraseToken,entry);
                                            found = true;
                                            return false; // early exit
                                        }
                                    });
                                }
                                if (!found) {
                                    console.log(`Failed to match ${tokenPrefix} to phrase "${phrase}"`); // FIXME: remove debug spam
                                }
                                else {
                                    // console.log(`${phraseEntry.phrase} changed to "${phrase}"`);
                                }
                            }
                            
                            _.forEach(coachVariables,(v,k)=>{
                                if (typeof v !== 'undefined') {
                                    phrase = phrase
                                    .split(`{${k}}`).join(v);
                                }
                            });
                            
                            // see if we have done this permutation before?
                            let existingInst;
                            if (!phraseEntry.instances) {
                                phraseEntry.instances = [];
                            }
                            else {
                                existingInst = _.find(phraseEntry.instances,{phrase});
                            }
                            if (!existingInst) {
                                let newInst = {phrase};
                                phraseEntry.instances.push(newInst);
                                VoiceStore.synthesizeAudio({ engine, voice_id, phrase, usageData})
                                .then((audio_src)=>{
                                    // console.log(`filling in audio_src for instance phrase "${phrase}"`);
                                    newInst.audio_src = audio_src;
                                });
                            }
                        }
                    }
                }
            }
        }
        i++;
        rollParams.action_index = i;
    } while(actionEntry);
    return null;
})


ShortCoursePlayStore.prototype.getVoiceElementCountForRichText = function(rich_text_id) {
    if(this.richTextVoiceData) {
        if (VoiceStore.getState().options.disabled) {
            return 0;
        }
        let voiceData = this.richTextVoiceData[rich_text_id];
        return voiceData ? voiceData.length : 0;
    }
    return 0;
}

// gets voice audio_src for a given rich text id
// returning a stream if the synthesizeAudio task doesn't have a result yet
// coachVariables is optional (will be gotten from playStore if not specified) 
ShortCoursePlayStore.prototype.getVoiceElement = function(rich_text_id, voice_element_index, coachVariables, rollParams) {
    const doc = this.doc;
    const { engine, voice_id } = doc.client_content.voiceOptions;
    const usageData = {
        channelId: this.getChannelId(),
        shortCourseId: this.getShortCourseId(),
    };

    if(this.richTextVoiceData) {
        let voiceData = this.richTextVoiceData[rich_text_id];
        if (voiceData && voiceData.length) {
            let phraseEntry = voiceData[voice_element_index];
            if (phraseEntry.audio_src) {
                return phraseEntry.audio_src;
            }
            let phrase = phraseEntry.phrase;
            if (phraseEntry.dynamic) {
                
                // see if it is a phrase set
                if (doc.client_content.phraseData) {
                    _.forEach(doc.client_content.phraseData.phraseSets,(entry)=>{
                        let phraseToken =`{${ShortCourseConstants.PHRASE_VARIABLE_PREFIX + entry.variableSuffix}}`;
                        if (phrase === phraseToken) {
                            phrase = this.lookupPhrase(rollParams,coachVariables,phrase,entry); // phrase = phraseSet token (voice support logic parses it so it is on a line by itself)
                            return false; // done searching
                        }
                    });
                }
                
                _.forEach(coachVariables,(v,k)=>{
                    if (typeof v !== 'undefined') {
                        phrase = phrase
                        .split(`{${k}}`).join(v);
                    }
                });
                let phraseInst = _.find(phraseEntry.instances,{phrase});
                if (phraseInst) {
                    if (phraseInst.audio_src) {
                        //console.log(`returning instance audio_src for ${phrase}`);
                        return phraseInst.audio_src;
                    }
                    //else {
                    //    console.log(`instance audio_src for ${phrase} not available`);
                    //}
                }
            }
            return VoiceStore.getStreamUrl({
                engine,
                voice_id,
                phrase,
                usageData
            });
        }
    }
    let rt = _.find(doc.client_content.rich_texts,{id:rich_text_id}); // FIXME: remove debug code
    console.log("getVoiceElement had no voice data for rich_text_id:",rich_text_id, rt); // FIXME: remove debug spam
    return null;
}

export default ShortCoursePlayStore;